import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { getCsrfToken } from '../utils/auth';
import TrainingCertificate from '../components/TrainingCertificate';
import { ExportModal } from '../components/ExportModal';
// The trace matrix's funnel, reused (not forked) — see the `chrome` note in that file.
import CoverageFunnel from '../components/FunnelView';
import useStickyHead from '../sbom/useStickyHead';
import '../styles/TrainingPanel.css';

/**
 * Training Traceability — the panel view. Always full size; there is no card → full-view step.
 *
 * Three lenses over ONE snapshot (built by POST /api/training/:id/build, read by GET .../report):
 *   Matrix   documents × the roles that actually have people, drill down to the people
 *   Funnel   where training leaks — and which team owns each drop
 *   Reports  people with issues · documents with issues · one person's training file
 *
 * The product is the MISSING cell: a person who holds a role their document names and has no
 * training task at all. They appear in no Orcanos report, because there is no record to report on.
 *
 * The reconciliation panel under the funnel is not decoration — the User Profile filter is the
 * ceiling on everything, so a snapshot that quietly drops evidence is worse than none
 * (Critical Note #24).
 */

const ST = {
  done: { cls: 'ok', glyph: '✓', label: 'Signed' },
  open: { cls: 'open', glyph: '◷', label: 'Open' },
  miss: { cls: 'miss', glyph: '?', label: 'Missing' },
  draft: { cls: 'draft', glyph: '·', label: 'Draft' },
};

/**
 * The four states a population splits into, in ONE order with ONE set of words — used by the
 * cell, the document rollup and the legend, so the three can never describe the same people
 * differently.
 *
 * The order is the auditor's, worst last. `draft` is a task that exists and was never
 * launched, which is why its word is not "open": nobody can sign it, so nothing is in
 * progress — it is closer to having no task than to having an unsigned one.
 */
const SPLIT = [
  { k: 'done', cls: 'ok', pill: 'p-ok', word: 'signed' },
  { k: 'open', cls: 'open', pill: 'p-open', word: 'in work' },
  { k: 'draft', cls: 'draft', pill: 'p-na', word: 'never launched' },
  { k: 'miss', cls: 'miss', pill: 'p-miss', word: 'no task' },
];

/**
 * {done, open, draft, miss} over a list of `cells`.
 *
 * One cell is one PERSON — the engine already de-duplicates somebody who holds two of the
 * document's roles (Critical Note #25) — so this counts people, and counts nobody twice.
 */
function splitOf(cells) {
  const n = { done: 0, open: 0, draft: 0, miss: 0 };
  (cells || []).forEach((c) => { n[c.st] = (n[c.st] || 0) + 1; });
  return n;
}

/**
 * The whole document in one number, across every role it names.
 *
 * The row's cells are per-role and the grid scrolls sideways, so which of them you can see
 * depends on your window — a document whose signatures sit in the roles off-screen read as
 * nothing but zeros. This says what the document itself is doing, and stays put.
 *
 * ⚠️ It is ONE bar, ONE percentage and the population it is out of — and deliberately nothing
 * else (product owner, 2026-09-03: *"I asked to add only a progress bar"*). The state breakdown
 * this used to print beside it now lives on the sub-line the row opens onto (`DocMetaLine`); a
 * collapsed row is meant to be scannable down a library of sixty documents, and six chips per
 * row is not. Anything added back here is another thing to read on every row.
 */
function DocRollup({ cells }) {
  if (!cells || !cells.length) return null;
  const n = splitOf(cells);
  // Deliberately ONE bar in ONE colour, not a stacked split of the four states. It answers the
  // only question asked at a glance — how much of this document's training is actually done —
  // and everything it leaves out is already beside it in the pills, in the per-role cells, and
  // person by person when the row is expanded.
  //
  // The denominator is every obligation this document creates: the people holding a role it
  // names AND the people it names individually, together, de-duplicated (Critical Note #44 —
  // scoring on roles alone hid trained people as "not configured"). So a document expecting 10
  // people with 8 tasks created and 2 signed reads 20% — a task that was never created is a
  // failure to train exactly as much as one that was never signed, and folding "created" into
  // the numerator would let a document reach 100% with nobody trained.
  const pct = Math.round((n.done / cells.length) * 100);
  return (
    // The bar carries a tooltip because a percentage alone doesn't say what it is OF; the pills
    // beside it deliberately don't — a number that only exists on hover is one most people
    // never read.
    <span className="tm-roll">
      <span
        className={`tm-docbar ${pct === 100 ? 'is-full' : ''}`}
        title={`${n.done} of ${cells.length} signed — everyone this document expects, `
          + 'roles and individually-named trainees together, on the last approved revision. '
          + 'A person with no task yet counts against it the same as an unsigned one.'}
      >
        <span className="tm-docbar-t"><i style={{ width: `${pct}%` }} /></span>
        <span className="tm-docbar-p">{pct}%</span>
      </span>
      {/* The denominator, always printed. A bare percentage cannot be audited — "25 %" of four
          people and of four hundred are different findings, and the second cannot be chased by
          hand. It is also what stops the bar reading as a share of the tasks that exist. */}
      <span
        className="tm-roll-n"
        title={`${cells.length} ${cells.length === 1 ? 'person' : 'people'} owe this training — `
          + 'everyone holding a role it names, plus anyone named individually, counted once each'}
      >
        of {cells.length}
      </span>
    </span>
  );
}

/**
 * Everything the row used to carry beside the bar, on the line the row opens onto.
 *
 * Product owner, 2026-09-03: the document line shows the key, the name, the revision, the bar
 * with its percentage and denominator, and the approval date — *"when I expand, add a sub line
 * that shows all the rest"*. So this is not a summary of the row; it is the row's chip run,
 * moved. Same chips, same words, same tooltips, same order — only the position changed, which is
 * why the copy here is duplicated from nowhere and edited in one place.
 *
 * It renders ABOVE the people, because it describes the document rather than any one of them.
 *
 * ⚠️ An attention row never gets one: it has nothing to expand (no cells, no history), so its
 * own tags — *No roles set*, *Not approved yet*, the role list, the routing state — must stay on
 * the line itself or they become unreachable. That is the trap in moving anything off a row:
 * whatever moves has to have somewhere to move TO.
 */
function DocMetaLine({ d, span }) {
  const n = splitOf(d.cells);
  return (
    <tr className="tm-dmeta-row">
      <td colSpan={span}>
        <div className="tm-dmeta">
          {/* Who has and has not signed, in words. The bar's percentage is the same fact
              rounded; this is the fact itself, and it is what gets chased. */}
          {SPLIT.filter((s) => n[s.k] > 0).map((s) => (
            <span key={s.k} className={`tm-pill ${s.pill}`}>{n[s.k]} {s.word}</span>
          ))}
          {d.src === 'formal' && d.category && (
            <span className="tm-cat" title="Training category">{d.category}</span>
          )}
          {d.src === 'formal' && d.department && (
            <span className="tm-dept" title="Department">{d.department}</span>
          )}
          {/* Not a contradiction — a finding. Carrying the record's verdict and the people's
              verdicts separately is the only way it can be seen at all. */}
          {d.complete_with_gaps && (
            <span
              className="tm-revnow"
              title="This record is signed off as complete, but trainees on it still have open or missing tasks. A training record is signed independently of its tasks, so the two can disagree."
            >
              Complete, with gaps
            </span>
          )}
          {/* The document has moved on to a newer revision that has not been approved yet.
              Shown, never scored on: the audience owes the approved revision, so treating this
              one as current would report everyone on a mid-routing document as untrained. */}
          {d.rev_current && (
            <span
              className="tm-revnow"
              title={d.rev
                ? `This document is now on revision ${d.rev_current}, which is still in routing — ${d.rev} is the last approved revision and is what training is judged against.`
                : `This document is on revision ${d.rev_current}, which has never been approved — so there is nothing released for anyone to train on yet.`}
            >
              Current rev {d.rev_current} — in routing
            </span>
          )}
          {(d.revisions || []).length > 0 && (
            <span
              className="tm-revcount"
              title={`${d.revisions.length} earlier ${d.revisions.length === 1 ? 'revision' : 'revisions'} with training tasks — open “Previous revisions” below`}
            >
              +{d.revisions.length} prev
            </span>
          )}
          {(d.orphan_roles || []).length > 0 && (
            <span className="tm-orphan" title={d.orphan_roles.join(', ')}>
              +{d.orphan_roles.length} roles with no user
            </span>
          )}
        </div>
      </td>
    </tr>
  );
}

/* ------------------------------------------------------------------- quiz
 *
 * The quiz is the SECOND half of a training obligation: reading the document and signing its
 * task is the training, the quiz checks what was understood. Everything below therefore sits
 * BESIDE the training state and never replaces it — a quiz result can no more promote a cell
 * here than it can in the engine (`quiz_overlay`), because "passed the quiz, never signed the
 * task" is a finding and the two verdicts have to be able to disagree in public.
 *
 * `locked` is deliberately not a failure colour. The person was never allowed to sit it.
 */
const QST = {
  passed: { cls: 'ok', glyph: '✓', label: 'Passed' },
  in_progress: { cls: 'open', glyph: '◷', label: 'In progress' },
  failed: { cls: 'open', glyph: '✗', label: 'Not passed' },
  exhausted: { cls: 'miss', glyph: '✗', label: 'No attempts left' },
  not_started: { cls: 'miss', glyph: '○', label: 'Not started' },
  locked: { cls: 'lock', glyph: '🔒', label: 'Locked' },
};

/**
 * Why the quiz columns can't say anything — the same three-way discrimination the period
 * control and the attention chips make, and for the same reason: "nobody owes a quiz" and
 * "this view cannot tell you about quizzes" are opposite claims, and showing a confident 0
 * for the second is the worse mistake on a compliance panel.
 *
 * `funnel.quiz` ABSENT ⇒ this report was rendered before quizzes existed (an old export, or a
 * backend that predates them). `configured: false` ⇒ the overlay ran and this account has
 * authored no quiz — an honest zero.
 */
function quizState(funnel) {
  const q = (funnel || {}).quiz;
  if (!q) return { on: false, why: 'This report was rendered before quizzes existed.', fix: 'Reload the panel to see quiz results.' };
  if (!q.configured) {
    return {
      on: false, q,
      why: 'No quiz has been written for any document yet.',
      fix: 'Create one from Quizzes to check what people understood after they sign.',
    };
  }
  return { on: true, q };
}

/**
 * Why a control beside this badge is greyed out — an icon, with the sentence on hover.
 *
 * The reason has to be SAYABLE (several browsers won't show a `title` on a disabled control,
 * and a dead chip with nothing beside it reads as a broken feature), but it does not have to
 * be shouted: printed in full these ran two lines across the filter row and pushed the real
 * controls off it. `tabIndex` and `aria-label` keep the sentence reachable without a mouse.
 */
function WhyBadge({ why, fix, label }) {
  const text = `${why} ${fix}`;
  return (
    <span className="tm-why" title={text} tabIndex={0} role="note" aria-label={text}>
      <span aria-hidden="true">⚠</span>{label ? <span className="tm-why-l">{label}</span> : null}
    </span>
  );
}

/** One person's quiz standing, as a chip. Renders nothing when no quiz is owed. */
function QuizChip({ q, compact }) {
  if (!q) return null;
  const s = QST[q.state] || QST.not_started;
  const score = (q.state === 'passed' || q.state === 'failed' || q.state === 'exhausted')
    && q.score != null ? ` ${q.score}%` : '';
  const tries = q.allowed ? ` · ${q.used} of ${q.allowed} ${q.allowed === 1 ? 'try' : 'tries'} used` : '';
  const title = q.state === 'locked'
    ? 'The quiz opens once this person signs their training task — reading and signing is the training, the quiz checks what was understood.'
    : q.state === 'passed'
      ? `Passed the quiz${q.score != null ? ` with ${q.score}%` : ''}${q.at ? ` on ${String(q.at).slice(0, 10)}` : ''}.`
        + (q.record && q.record !== 'written'
          ? ' The pass has not been written back to Orcanos yet — it is recorded here.' : '')
      : `${s.label}${tries}.`;
  return (
    <span className={`tm-q tm-q-${s.cls}`} title={title}>
      <span className="tm-g">{s.glyph}</span>
      {compact ? '' : 'Quiz '}{s.label}{score}
      {/* A pass that has not reached Orcanos is the one thing about a quiz that is a
          compliance finding rather than a score, so it is never folded into the label. */}
      {q.state === 'passed' && q.record && q.record !== 'written' && (
        <span className="tm-q-pending" title="Passed here; the training record has not been written back to Orcanos yet.">
          not written back
        </span>
      )}
    </span>
  );
}

/**
 * A document- or revision-level quiz tag — 🎓 Quiz 3/7.
 *
 * It becomes a BUTTON that opens the quiz when `onOpen` is given, and stays a plain chip
 * otherwise. `onOpen` is only given to an Orcanos administrator, because the quiz screens
 * are admin-only (`App.jsx`) — rendering the link for anyone else would hand them a click
 * that bounces them back to the training home with no explanation.
 *
 * `stopPropagation` is load-bearing: the document row underneath is itself a toggle, so
 * without it every attempt to open a quiz would collapse the row instead.
 */
function QuizTag({ cls = '', title, onOpen, children }) {
  if (!onOpen) return <span className={`tm-qtag ${cls}`} title={title}>{children}</span>;
  return (
    <button
      type="button"
      className={`tm-qtag is-link ${cls}`}
      title={`${title} — click to open it`}
      onClick={(e) => { e.stopPropagation(); onOpen(); }}
    >
      {children}
    </button>
  );
}

/** `C.1` and `C.2` are both revision C — the letter is what a document's revision field holds. */
const majorOf = (rev) => {
  const m = /^([A-Za-z]{1,3})(?:\.\d{1,3})?$/.exec(String(rev || '').trim());
  return m ? m[1].toUpperCase() : null;
};

/** Sorts AA above Z, which a plain string comparison gets wrong. */
const revRank = (a) => [String(a || '').length, String(a || '').toUpperCase()];
const revAbove = (a, b) => {
  const [la, sa] = revRank(a);
  const [lb, sb] = revRank(b);
  return la !== lb ? la > lb : sa > sb;
};

/**
 * The build reports one line, e.g.
 *   "Documents 200/340 · User profiles 6/6 · Training tasks waiting"
 * Split it into a row per source so you can see which filter is the slow one.
 * Anything unparseable falls back to being shown as plain text.
 */
function parseProgress(message) {
  if (!message || !message.includes('·')) return [];
  return message.split('·').map((raw) => {
    const part = raw.trim();
    const m = part.match(/^(.*?)\s+([\d,]+)\/([\d,]+)$/);
    if (m) {
      const n = Number(m[2].replace(/,/g, ''));
      const total = Number(m[3].replace(/,/g, ''));
      return {
        label: m[1],
        detail: `${m[2]} of ${m[3]}`,
        pct: total ? Math.min(100, Math.round((n / total) * 100)) : 0,
        done: total > 0 && n >= total,
      };
    }
    const w = part.match(/^(.*?)\s+(waiting|none)$/);
    if (w) {
      return {
        label: w[1],
        detail: w[2] === 'none' ? 'no items' : 'waiting…',
        pct: 0,
        done: w[2] === 'none',
      };
    }
    return { label: part, detail: '', pct: 0, done: false };
  }).filter((p) => p.label);
}

/**
 * One person's line inside an expanded document — used for the current revision and for every
 * superseded one, so the two can never drift apart.
 *
 * ⚠️ The state shown is `routing` (DMS_Routing_State). `status` is Obj_status — the task's
 * CATEGORY ("Read And Understand" / "In Process"), not whether it is done, and showing it here
 * read as a state it never was (Critical Note #24, trap 3).
 */
function PersonRow({ name, role, st, task, span, docRev, extra, nested, onCertify, quiz }) {
  const s = ST[st] || ST.miss;
  const rev = task && task.rev;
  // `draft` had no branch here and fell through to "no task" — which is a different finding
  // and the wrong one: the task exists, it was simply never launched, so it can never be
  // signed and the person will never be chased for it.
  const label = st === 'done'
    ? `signed${rev ? ` · rev ${rev}` : ''}`
    : st === 'open'
      ? `${(task && task.ts) || 'open'}${task && task.due ? ` · due ${task.due}` : ''}`
      : st === 'draft'
        ? `${(task && task.ts) || 'draft'} · never launched`
        : 'no task';
  // A task on a revision NEWER than the last approved one still satisfies today's requirement
  // (four documents on orca60 have their whole wave on a revision still in routing) — but say
  // so, or the row silently reads as if it were signed on the released revision.
  const ahead = docRev && rev && revAbove(majorOf(rev), docRev);
  return (
    <tr className={`tm-kid ${st === 'miss' ? 'is-miss' : ''} ${nested ? 'tm-kid-deep' : ''}`}>
      <td colSpan={span}>
        <div className="tm-dc">
          <span className="tm-un">{name}</span>
          <span className="tm-ur">{role}</span>
          <span className={`tm-us ${s.cls}`}>
            <span className="tm-g">{s.glyph}</span>{label}
          </span>
          {ahead && (
            <span className="tm-revtag" title={`Rev ${majorOf(rev)} is newer than the last approved revision (${docRev}) — it is still in routing.`}>
              rev {majorOf(rev)} — not yet approved
            </span>
          )}
          {/* Beside the signature, never instead of it. A pass is a second fact about this
              person on this revision, and the row has to be able to show both. */}
          <QuizChip q={quiz} />
          {/* Only a signature can be certified — there is nothing to certify about an open or
              missing task, and a sheet for one would read as proof of the opposite.
              Rendered BEFORE the task key on purpose: the key is pinned right (`margin-left:
              auto`), so anything after it rides the right edge too and the button would land in
              a different place on every row. */}
          {st === 'done' && onCertify && (
            <button
              type="button"
              className="tm-certlink"
              onClick={onCertify}
              title="Printable completion certificate for this signature"
            >
              📜 Certificate
            </button>
          )}
          {task && task.tk && (
            task.turl ? (
              <a
                className="tm-tk tm-link"
                href={task.turl}
                target="_blank"
                rel="noopener noreferrer"
                title="Open this training task in Orcanos"
              >{task.tk}</a>
            ) : <span className="tm-tk">{task.tk}</span>
          )}
          {extra}
        </div>
      </td>
    </tr>
  );
}

/**
 * The superseded revisions of one document, newest first (D, C, B, A).
 *
 * Two different questions are being answered above and below this line, and mixing them would
 * make the headline number mean neither: the row above is "are we compliant with what is
 * released TODAY", these are "what happened on the releases before it".
 *
 * A person who joined after a revision was released never owed its training — they are counted
 * as *not yet joined*, never as missing. Where either date is unreadable the engine says
 * *unknown* rather than guessing in either direction.
 */
function RevisionHistory({ doc, span, onCertify, onOpenQuiz }) {
  const [openHist, setOpenHist] = useState(false);
  const [openRev, setOpenRev] = useState({});
  const revs = doc.revisions || [];
  if (!revs.length) return null;

  const totals = revs.reduce((a, r) => ({
    miss: a.miss + (r.counts.miss || 0),
    na: a.na + (r.counts.na || 0),
    unknown: a.unknown + (r.counts.unknown || 0),
  }), { miss: 0, na: 0, unknown: 0 });

  return (
    <>
      <tr className="tm-kid tm-histrow">
        <td colSpan={span}>
          <button className="tm-histbtn" onClick={() => setOpenHist((v) => !v)}>
            <span className={`tm-chev ${openHist ? 'is-open' : ''}`}>▶</span>
            Previous revisions
            <span className="tm-histn">{revs.length}</span>
          </button>
          {!openHist && totals.miss > 0 && (
            <span className="tm-hist-sub tm-hist-miss">{totals.miss} never trained on a revision they were here for</span>
          )}
          {!openHist && totals.na > 0 && (
            <span className="tm-hist-sub">{totals.na} joined after a revision was released</span>
          )}
          {!openHist && totals.unknown > 0 && (
            <span className="tm-hist-sub">{totals.unknown} can’t be judged — no joining date</span>
          )}
        </td>
      </tr>

      {openHist && revs.map((r) => {
        const isOpen = !!openRev[r.rev];
        const c = r.counts;
        return (
          <React.Fragment key={r.rev}>
            <tr className="tm-kid tm-revhead">
              <td colSpan={span}>
                <div className="tm-dc">
                  <button className="tm-histbtn" onClick={() => setOpenRev((o) => ({ ...o, [r.rev]: !o[r.rev] }))}>
                    <span className={`tm-chev ${isOpen ? 'is-open' : ''}`}>▶</span>
                    Rev {r.rev}
                  </button>
                  <span className="tm-revmeta">
                    {r.date ? (
                      <span
                        title={r.date_src === 'due'
                          ? 'Dated by the earliest due date of this revision’s tasks — the Training Task filter has no Created Date column, so this is later than the real release.'
                          : 'The earliest training task raised on this revision.'}
                      >
                        {r.date_src === 'due' ? '≈ ' : ''}{r.date}
                      </span>
                    ) : <span className="tm-muted">date unknown</span>}
                    {r.labels && r.labels.length > 1 && (
                      <span className="tm-muted"> · {r.labels.join(', ')}</span>
                    )}
                    <span className="tm-muted"> · {r.tasks} {r.tasks === 1 ? 'task' : 'tasks'}</span>
                  </span>
                  <span className="tm-revcounts">
                    {c.done > 0 && <span className="tm-pill p-ok">{c.done} signed</span>}
                    {c.open > 0 && <span className="tm-pill p-open">{c.open} open</span>}
                    {c.miss > 0 && <span className="tm-pill p-miss">{c.miss} missing</span>}
                    {c.na > 0 && (
                      <span className="tm-pill p-na" title={`Joined after this revision was released: ${r.na_names.join(', ')}`}>
                        {c.na} not yet joined
                      </span>
                    )}
                    {c.unknown > 0 && (
                      <span className="tm-pill p-unk" title={`No joining date, so it can’t be said whether they owed this revision: ${r.unknown_names.join(', ')}`}>
                        {c.unknown} unknown
                      </span>
                    )}
                    {/* The quiz that was filed under THIS revision — usually retired when the
                        next one was released, which is the healthy case and not a fault. It is
                        the record of a wave that has already happened, so it is counted in
                        nothing above: today's obligation is the current revision's quiz. */}
                    {r.quiz && (
                      <QuizTag
                        cls={r.quiz.eligible && r.quiz.passed >= r.quiz.eligible ? 'is-ok' : ''}
                        onOpen={onOpenQuiz ? () => onOpenQuiz(r.quiz.id, 'results') : null}
                        title={`“${r.quiz.title}” — the quiz for revision ${r.quiz.revision || r.rev}, `
                          + `${r.quiz.questions} question${r.quiz.questions === 1 ? '' : 's'}, pass mark `
                          + `${r.quiz.pass_mark}%. ${r.quiz.passed} of ${r.quiz.eligible} who signed this `
                          + `revision passed it`
                          + (r.quiz.locked ? `; ${r.quiz.locked} never signed, so it never opened for them` : '')
                          + (r.quiz.status === 'superseded'
                            ? '. Retired when the next revision was released — this is the record of it, not work anybody owes now.'
                            : '.')}
                      >
                        <span className="tm-qtag-i" aria-hidden="true">🎓</span>
                        Quiz {r.quiz.passed}/{r.quiz.eligible}
                        {r.quiz.status === 'superseded' ? '' : ` · rev ${r.quiz.revision || r.rev}`}
                      </QuizTag>
                    )}
                  </span>
                </div>
              </td>
            </tr>

            {isOpen && r.cells.map((p, i) => (
              <PersonRow
                key={`${r.rev}-${i}`}
                name={p.n}
                role={p.role}
                st={p.st}
                task={p.tk ? p : null}
                span={span}
                nested
                /* Their standing on the quiz for THIS revision — beside the signature,
                   never instead of it, exactly as on the current revision. */
                quiz={p.q}
                onCertify={onCertify
                  ? () => onCertify({ name: p.n, uid: p.u, role: p.role }, p)
                  : null}
              />
            ))}
            {isOpen && (r.na_names.length > 0 || r.unknown_names.length > 0) && (
              <tr className="tm-kid tm-revnote">
                <td colSpan={span}>
                  {r.na_names.length > 0 && (
                    <span>
                      <b>Not expected:</b> {r.na_names.join(', ')} — joined after this revision
                      was released{r.date ? ` (${r.date})` : ''}.
                    </span>
                  )}
                  {r.unknown_names.length > 0 && (
                    <span>
                      <b>Can’t tell:</b> {r.unknown_names.join(', ')} — no joining date
                      {r.date ? '' : ', and this revision has no date either'}.
                    </span>
                  )}
                </td>
              </tr>
            )}
          </React.Fragment>
        );
      })}
    </>
  );
}

const countBy = (doc, st) => doc.cells.filter((c) => c.st === st).length;
const pctColor = (p) => (p < 50 ? 'var(--tm-miss-fg)' : p < 90 ? 'var(--tm-open-fg)' : 'var(--tm-ok-fg)');

function Meter({ pct }) {
  return (
    <div className="tm-meter">
      <div className="tm-meter-track">
        <i style={{ width: `${pct}%`, background: pctColor(pct) }} />
      </div>
      <span className="tm-meter-pc">{pct}%</span>
    </div>
  );
}

/**
 * A quiz TALLY for one row of a report table — a document, a role, a person.
 *
 * `passed of eligible`, and the two are different denominators on purpose: somebody who has
 * not signed their task yet was never allowed to sit the quiz, so counting them as an
 * outstanding quiz would report the same person twice — once as an unsigned task, once as a
 * failed comprehension check — under two owners who would each think it was the other's.
 * They are named separately, in the tooltip and as a small "n to sign" note.
 */
function QuizTally({ q }) {
  if (!q || !q.expected) return <span className="tm-dash">—</span>;
  const outstanding = (q.eligible || 0) - (q.passed || 0);
  const cls = !q.eligible ? 'p-na' : outstanding > 0 ? 'p-open' : 'p-ok';
  return (
    <span
      className="tm-qtally"
      title={`${q.passed} of ${q.eligible} who can sit the quiz have passed it`
        + (q.locked ? `; ${q.locked} have not signed their task yet, so it has not opened for them` : '')
        + (q.exhausted ? `; ${q.exhausted} have used every attempt without passing` : '')
        + '.'}
    >
      <span className={`tm-pill ${cls}`}>{q.passed}/{q.eligible}</span>
      {q.locked > 0 && <span className="tm-qlock">{q.locked} to sign</span>}
    </span>
  );
}

/* ------------------------------------------------------------------ matrix */

/* Same pair of glyphs the trace matrix uses for its one expand/collapse-all toggle, so the
   two grids read identically. */
const UnfoldMoreIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M4 5 7 2l3 3M4 9l3 3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const UnfoldLessIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M4 2.5 7 5.5l3-3M4 11.5l3-3 3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
/* The funnel glyph, not a sliders glyph — the row it opens is a set of population filters. */
const FilterIcon = () => (
  <svg viewBox="0 0 14 14" fill="none" aria-hidden="true">
    <path d="M1.5 2.5h11L8.2 7.6v4L5.8 12.6V7.6L1.5 2.5Z" stroke="currentColor" strokeWidth="1.4" strokeLinejoin="round" />
  </svg>
);

const PAGE_SIZES = [25, 50, 100];
const PAGE_SIZE_KEY = 'trainingRowsPerPage';
// "Only roles with users" — a VIEW preference (which columns are drawn), so it is remembered
// per browser like the page size, and not part of the query the server filters on.
const PEOPLED_ROLES_KEY = 'trainingOnlyPeopledRoles';

// Insertion order is the order the chips render in.
//
// The last two are a different KIND of finding from the first two: `miss`/`open` are compliance
// (a person owes training), `noroles`/`notappr` are configuration (the document can't be scored
// at all). They live on the same row because the question — "show me everything wrong with my
// training" — is one question, and because a document that names no role is the most complete
// failure of the lot: Orcanos reports nothing on it, so it reads as fine everywhere else.
const FILTER_LABEL = {
  all: 'All',
  miss: 'Missing only',
  open: 'Has open',
  // A THIRD kind of finding: the training was signed and the quiz that checks it was not
  // passed. It sits with the others because "show me everything wrong with my training" is
  // one question — and because this is the only gap the signature alone will never reveal.
  quizgap: 'Quiz outstanding',
  // The one chip on this row that is a LENS rather than a finding: everything a quiz covers,
  // healthy or not. It answers the question the other quiz chip cannot — *which* of my
  // documents have a quiz at all — which is what somebody rolling quizzes out is actually
  // working from, and which the grid otherwise only shows by scanning every row for a tag.
  hasquiz: 'Has a quiz',
  noroles: 'Missing roles',
  notappr: 'Not approved',
};

/* The chip LABELS. The predicates behind them live on the server (`training_query.FILTER_FN`)
   and deliberately do not exist here as well: the grid asks for `state=miss` and is handed the
   rows, so there is one definition of what each chip means. Two implementations of a compliance
   filter drifting apart is the trap Critical Note #22 records for the two scorers. A chip added
   here must be added there — and its count comes back in `counts`, so a chip with no server-side
   predicate simply reads 0 rather than silently filtering nothing. */

const ATTN_LABEL = {
  no_roles: 'No roles set',
  not_approved: 'Not approved yet',
  no_audience: 'No roles or trainees',
};

/* ------------------------------------------------------- source
 *
 * Documents and formal training records are two populations in ONE list, told apart by `src`
 * (the engine keeps them together because every compliance figure sums over `docs[].cells` —
 * a second list would mean a second, silently-diverging set of totals).
 *
 * So the source is a FILTER, and it narrows the population BEFORE the period and the state
 * chips count — the same discipline the period filter already follows. That is what lets "Both"
 * be an honest combined number rather than a total that quietly counts an obligation twice.
 *
 * Applied on the server (`training_query.SOURCE_FN`), which is also where the pre-v3.22.0
 * default lives: a snapshot with no `src` at all reads as `doc`, never as neither.
 */
const SOURCE_LABEL = { all: 'Both', doc: 'Documents', formal: 'Formal training' };

/* ------------------------------------------------------- period
 *
 * Ranges over `approved_iso` — the engine's parsed twin of the verbatim date it prints.
 * Comparing the rendered string would be wrong: a filter result reads day-first and an item read
 * through QW_Get_Object reads month-first, so the engine settles the order once per source and
 * hands over ISO.
 *
 * The wording is deliberately NEUTRAL ("Any date", not "Any approval date"): the date it ranges
 * over is a document's APPROVAL date and a formal record's COMPLETION date. Both are the moment
 * the thing became trainable-on, which is why one control can serve both — but a label reading
 * "approved" over a list containing completion dates would be one word making a column mean two
 * things. Each row prints which of the two its own date is.
 *
 * These are CALENDAR periods, not rolling windows — "this month" is the month, "last week" is
 * the previous Monday–Sunday. Which of the two a label means is exactly the thing people
 * disagree about, so the resolved range is printed beside the control rather than left to
 * inference.
 */
const PERIODS = [
  ['all', 'Any date'],
  ['today', 'Today'],
  ['week', 'Last week'],
  ['month', 'This month'],
  ['year', 'This year'],
  ['lastyear', 'Last year'],
  ['custom', 'Custom range…'],
];

/* The resolved range is NOT computed here. The server returns the `[from, to]` it actually
   filtered on, and that is what the control prints. Working it out a second time in the
   browser would put a calendar calculation in two places, and the failure mode is a label
   that confidently states a window different from the one the rows came from. */

/**
 * ℹ beside the "Documents × role" title — what the grid counts, as a small card. Replaces the
 * footnote paragraph under the grid and the "Click a row…" note (2026-11-02, user: the bottom
 * text was unreadable). Same facts, laid out: four figures, then one short line per rule.
 */
function DocsInfo({ funnel, qf }) {
  // The card is portalled with a fixed position: .tm-panel clips overflow, so a card inside it
  // is cut off on a short table. Any scroll closes it rather than leaving it floating.
  const [pos, setPos] = useState(null);
  const open = !!pos;
  const ref = useRef(null);
  const popRef = useRef(null);
  const toggle = () => {
    if (open) { setPos(null); return; }
    const r = ref.current.getBoundingClientRect();
    setPos({ top: r.bottom + 6, left: Math.max(16, Math.min(r.left - 8, window.innerWidth - 396)) });
  };
  useEffect(() => {
    if (!open) return undefined;
    const close = () => setPos(null);
    const onDown = (e) => {
      if (ref.current?.contains(e.target) || popRef.current?.contains(e.target)) return;
      close();
    };
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [open]);

  const rev = funnel.revisions;
  const quiz = qf && qf.expected > 0;
  return (
    <span className="tm-hinfo" ref={ref}>
      <button
        type="button"
        className={`tm-hinfo-btn${open ? ' on' : ''}`}
        onClick={toggle}
        aria-expanded={open}
        aria-label="About this table"
        title="About this table"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
          strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
      </button>
      {open && createPortal(
        <div className="tm-hinfo-pop" role="dialog" aria-label="About this table" ref={popRef}
          style={{ top: pos.top, left: pos.left }}>
          <div className="tm-hinfo-title">About this table</div>
          <div className="tm-hinfo-stats">
            <div><b>{funnel.dms_total}</b><span>DMS items</span></div>
            <div><b>{funnel.users}</b><span>User profiles</span></div>
            <div><b>{funnel.tasks_total}</b><span>Training tasks</span></div>
            <div className="hl"><b>{funnel.in_scope}</b><span>Documents in scope</span></div>
          </div>
          <ul className="tm-hinfo-list">
            <li><b>In scope</b> — an approved revision and an audience (Roles, named Trainees, or both).</li>
            <li><b>Current revision only</b> — signing an older one counts as untrained.</li>
            {rev && rev.revisions > 0 && (
              <li><b>{rev.revisions} earlier revisions</b> in {rev.docs_with_history} documents —
                expand a row, then <i>Previous revisions</i>.</li>
            )}
            {quiz && (
              <li><b>{qf.published} {qf.published === 1 ? 'quiz' : 'quizzes'}</b> on {qf.docs_with_quiz}{' '}
                {qf.docs_with_quiz === 1 ? 'document' : 'documents'} — shown beside the signature, never
                instead of it.</li>
            )}
            <li><b>Click a row</b> to see the people behind the numbers.</li>
          </ul>
        </div>,
        document.body,
      )}
    </span>
  );
}

function MatrixView({
  report, search, setupId, panelName, builtAt,
  // Fit to screen (toolbar ⇥⇤): compact Documents × role grid — narrow role columns, wrapped
  // role names, the per-state words moved to the cell tooltip. Display only.
  fit = false,
  // Opens a quiz from the grid — given only to an Orcanos administrator, since the quiz
  // screens are admin-only. Undefined ⇒ the quiz tags stay plain chips.
  onOpenQuiz,
  // The filter row is collapsed by default and toggled from the toolbar (the ▽ beside the search
  // box), so its open state lives up there. `onFilterSummary` reports back what is currently
  // narrowing the list, which is what the collapsed toggle prints on its face.
  filtersOpen = true, onFilterSummary,
}) {
  const { funnel } = report;
  // The role header also follows the PAGE scroll (the box below only sticks it inside itself).
  const headBox = useStickyHead();

  /* ------------------------------------------------------------------ the rows
   *
   * The grid's rows, its chip counts and its role columns all come from the SERVER now
   * (`GET /:id/documents`, backed by `training_query.py`). They used to be derived here by
   * filtering `report.docs` in the browser, which meant the snapshot had to be shipped whole
   * — and `docs[].cells` carries one entry per (document x role x person) obligation, so a
   * library of a few thousand documents is tens of megabytes across the wire to draw 25 rows.
   *
   * The pipeline was MOVED, not copied: there is no filter predicate left on this side. Two
   * implementations of a compliance filter drifting apart is the trap Critical Note #22
   * records for the two scorers, and the rule now lives in exactly one place.
   */
  const [source, setSource] = useState('all');
  const [filter, setFilter] = useState('all');
  const [open, setOpen] = useState({});
  // { person, doc, task } for the one-training certificate opened from a signed drill-down row.
  const [cert, setCert] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = parseInt(localStorage.getItem(PAGE_SIZE_KEY), 10);
    return PAGE_SIZES.includes(saved) ? saved : 25;
  });
  // Hide role columns nobody in this panel holds. Off by default: hiding a column is hiding
  // data, and that has to be something the user asked for — but it is remembered once asked.
  const [peopledOnly, setPeopledOnly] = useState(
    () => localStorage.getItem(PEOPLED_ROLES_KEY) === '1',
  );
  const togglePeopledOnly = () => setPeopledOnly((v) => {
    localStorage.setItem(PEOPLED_ROLES_KEY, v ? '0' : '1');
    return !v;
  });

  const q = search.trim().toLowerCase();

  // The approval period narrows the POPULATION the chips and the search then work over, so the
  // chip counts stay true to what is on screen instead of counting documents the period hides.
  const [period, setPeriod] = useState('all');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  /* The search now costs a request, so it is debounced — every keystroke firing its own fetch
     would put the answers in a race with each other as well as wasting them. The resolved
     range is still computed here purely to LABEL the control ("Approved 2026-01-01 → …");
     the server computes its own from the same period key and is what actually filters. */
  const [qLive, setQLive] = useState(q);
  useEffect(() => {
    const t = setTimeout(() => setQLive(q), 250);
    return () => clearTimeout(t);
  }, [q]);


  const [data, setData] = useState(null);
  const [loadError, setLoadError] = useState('');
  const seqRef = useRef(0);

  // Any change to what is being asked for goes back to page 1 — staying on page 7 of a
  // narrower list is how a filter looks like it returned nothing.
  useEffect(() => { setPage(1); }, [filter, qLive, pageSize, period, from, to, source]);

  useEffect(() => {
    // Requests are numbered and only the newest may write. Without this a slow early
    // request can land after a fast later one and quietly restore the previous filter's
    // rows — the same class of bug as the export poller's re-entrancy guard (v3.8.5).
    const seq = ++seqRef.current;
    const params = new URLSearchParams({
      page: String(page), limit: String(pageSize), scope: 'matrix',
      source, period, from, to, q: qLive, state: filter,
    });
    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(`/api/training/${setupId}/documents?${params.toString()}`,
                                 { credentials: 'include' });
        const d = await resp.json().catch(() => ({}));
        if (cancelled || seq !== seqRef.current) return;
        if (!resp.ok) throw new Error(d.detail || `(${resp.status})`);
        setData(d);
        setLoadError('');
      } catch (e) {
        if (cancelled || seq !== seqRef.current) return;
        // The panel keeps whatever it last drew rather than blanking: a stale page with a
        // banner over it is more use than an empty grid that reads as "no documents".
        setLoadError(`Could not read this page — ${e.message || 'the request failed'}.`);
      }
    })();
    return () => { cancelled = true; };
  }, [setupId, page, pageSize, source, period, from, to, qLive, filter]);

  /* Everything below reads the server's answer. Each `?? ` fallback is the pre-first-response
     value, not a second implementation of the number. */
  const pageRows = useMemo(() => (data && data.rows) || [], [data]);
  const counts = (data && data.counts) || {};
  const sourceCounts = (data && data.sources) || {};
  const formalAvailable = !!(data && data.formal_available);
  const roleCols = useMemo(() => (data && data.role_cols) || [], [data]);
  const total = (data && data.total) || 0;
  const scopedTotal = (data && data.scoped_total) || 0;
  const scopedAttn = (data && data.scoped_attn) || 0;
  const scopedScored = scopedTotal - scopedAttn;
  const inPeriodCount = (data && data.in_period) || 0;
  const searchHitCount = (data && data.search_hits) || 0;
  const hiddenByFilter = (data && data.hidden_by_filter) || 0;
  const datedDocs = (data && data.dated) || 0;
  // A document with no readable approval date can't be placed in any range. It is excluded, and
  // COUNTED — on a compliance panel a row that quietly disappears is worse than one that says why.
  // A document that was never approved has no such date BY DEFINITION, so every approval period
  // hides the whole "Not approved" list; that is the honest answer, and the count says so.
  const undated = (data && data.undated) || 0;
  // The window the server actually filtered on — see the note above `PERIODS`.
  const range = (data && data.range) || null;
  // The UNFILTERED number of rows that owe training and cannot be scored. It has to be the
  // panel-wide fact, not a page's worth, because it is what tells "0 such documents" apart
  // from "this panel cannot tell you" (Critical Note #24).
  const attentionTotal = report.attention_total != null
    ? report.attention_total : (report.attention || []).length;

  /**
   * Why the period control can't be used — the three cases are genuinely different and the
   * fixes are different, so a single "no approval-date column" message would be a claim the
   * UI has not checked.
   *
   * `funnel.approved_field` only exists on a snapshot built by an engine that knows about
   * approval periods: **absent** means the snapshot predates the feature (rebuild), **null**
   * means the engine looked and the Document filter carries no such column (fix the filter).
   */
  const periodBlocked = useMemo(() => {
    if (datedDocs > 0) return null;
    if (!('approved_field' in (funnel || {}))) {
      return {
        why: 'This report was built before approval dates were added.',
        fix: 'Rebuild it with ⟳ to filter by approval period.',
      };
    }
    if (!funnel.approved_field) {
      return {
        why: 'Your Document filter has no Approved Date (Routing End Date) column.',
        fix: 'Add that column to the filter in Orcanos, then rebuild.',
      };
    }
    return {
      why: 'None of the documents has a readable approval date.',
      fix: 'Check the Approved Date column in Orcanos, then rebuild.',
    };
  }, [datedDocs, funnel]);

  /**
   * Why the two configuration chips can't be used. Same three-way discrimination the period
   * control makes, and for the same reason: "0 documents are missing their roles" and "this
   * panel cannot tell you whether any document is missing its roles" are opposite claims, and
   * showing a confident 0 for the second one is the worse mistake on a compliance panel.
   *
   * `training_list_field` **absent** ⇒ the snapshot predates the feature (rebuild).
   * **null** ⇒ the engine looked and the Document filter carries no Training List column, so it
   * cannot tell a document that owes training from one that needs none (fix the filter).
   */
  const attnBlocked = useMemo(() => {
    if (attentionTotal > 0) return null;
    if (!('training_list_field' in (funnel || {}))) {
      return {
        why: 'This report was built before documents without roles were listed.',
        // Deliberately not the period control's "Rebuild it with ⟳" wording: both can be
        // blocked on the same stale snapshot, and two identical sentences side by side read
        // as one duplicated message rather than two separate things to fix.
        fix: 'Rebuild the report with ⟳ to see them.',
      };
    }
    if (!funnel.training_list_field) {
      return {
        why: 'Your Document filter has no Training List column, so a document that owes training '
          + 'can’t be told from one that needs none.',
        fix: 'Add that column to the filter in Orcanos, then rebuild.',
      };
    }
    return null;      // the column is there and nothing is wrong — an honest zero
  }, [attentionTotal, funnel]);

  /* Whether this panel can say anything about quizzes at all, and why not when it can't.
     Quiz standing is overlaid onto the snapshot at READ time (attempts change by the minute
     and a rebuild takes minutes), so this is current even on an old snapshot. */
  const quizInfo = useMemo(() => quizState(funnel), [funnel]);
  const qf = quizInfo.on ? quizInfo.q : null;

  /* The server clamps a page past the end and returns the page it actually served, so these
     two are ITS answer rather than a second calculation over a list this side no longer has.
     Deriving them here from `total` would be the same arithmetic in a second place, and the
     two would disagree for exactly one render after every filter change. */
  const totalPages = (data && data.pages) || 1;
  const safePage = (data && data.page) || 1;
  const start = (data && data.start) || 0;

  const changePageSize = (n) => {
    setPageSize(n);
    localStorage.setItem(PAGE_SIZE_KEY, String(n));
  };

  // One button does both, exactly as the trace matrix does it: it collapses only once EVERY
  // document on the page is open, so a partly-expanded page opens the rest first rather than
  // shutting the ones you just opened.
  //
  // Scoped to the page, not the whole filtered set — expanding rows that aren't rendered buys
  // nothing and would make "Collapse all" look broken after a page change. `open` is keyed by
  // document key and left alone for other pages, so paging back keeps what you had open.
  const allOpen = pageRows.length > 0 && pageRows.every((d) => open[d.key]);
  const toggleAll = () => {
    if (allOpen) { setOpen({}); return; }
    setOpen((o) => {
      const next = { ...o };
      pageRows.forEach((d) => { next[d.key] = true; });
      return next;
    });
  };

  /* How many people hold each role — the count under each column header.
     Taken from the panel-wide role aggregate rather than from the rows on screen: a page is
     25 documents and the header describes the ROLE, so counting the page would make the same
     column header change every time you turn a page. Falls back to the rows only for a caller
     that still passes a whole report (`docs=all`, i.e. an older client path). */
  const peoplePerRole = useMemo(() => {
    const n = {};
    const agg = report.role_report;
    if (agg) {
      agg.forEach((r) => { n[r.role] = r.people; });
      return n;
    }
    (report.docs || pageRows).forEach((d) => roleCols.forEach((r) => {
      if ((d.cols || {})[r]) n[r] = d.cols[r].users.length;
    }));
    return n;
  }, [report.role_report, report.docs, pageRows, roleCols]);

  /* "Only roles with users" — the columns actually drawn.
   *
   * A library can name dozens of roles, and a role nobody in this panel holds contributes an
   * all-blank column that pushes the ones with signatures off the right-hand edge (the grid
   * scrolls sideways — Critical Note #25's cousin: what you can see depends on your window).
   *
   * The predicate is the SAME number the column header prints, so a column is hidden exactly
   * when its header would have read "0 people" — no second definition of "empty" to drift.
   * It is a VIEW control only: nothing about the rows, the chip counts or the KPIs changes,
   * and the count of what it hid is printed beside it so a narrowed grid says so out loud.
   */
  const emptyRoleCount = useMemo(
    () => roleCols.filter((r) => !(peoplePerRole[r] > 0)).length,
    [roleCols, peoplePerRole],
  );
  const visibleRoleCols = useMemo(
    () => (peopledOnly ? roleCols.filter((r) => peoplePerRole[r] > 0) : roleCols),
    [peopledOnly, roleCols, peoplePerRole],
  );

  /* What is narrowing the list right now, in the chips' own words — empty when nothing is.
     The toolbar toggle prints this while the row is collapsed, so a shortened list always
     carries its reason even when the control that caused it is off screen.
     Declared here, below the role columns, because it reports the hidden-column count too. */
  useEffect(() => {
    if (!onFilterSummary) return;
    const bits = [];
    if (formalAvailable && source !== 'all') bits.push(SOURCE_LABEL[source]);
    if (filter !== 'all') bits.push(FILTER_LABEL[filter]);
    if (range) {
      const p = PERIODS.find(([k]) => k === period);
      bits.push(period === 'custom'
        ? `Approved ${range[0] || '…'} → ${range[1] || '…'}`
        : (p ? p[1] : period));
    }
    // Hidden COLUMNS belong here too — the filter row is collapsed by default, so this is the
    // only place a grid missing role columns can say that it is missing them on purpose.
    if (peopledOnly && emptyRoleCount > 0) {
      bits.push(`${emptyRoleCount} empty role${emptyRoleCount === 1 ? '' : 's'} hidden`);
    }
    onFilterSummary(bits.join(' · '));
  }, [filter, period, range, source, formalAvailable, peopledOnly, emptyRoleCount,
      onFilterSummary]);

  /**
   * One (document × role) cell: signed-of-expected, the states that make up the rest, and a
   * bar in the same four colours.
   *
   * The fraction on its own answered "how many signed" and hid WHY the others hadn't — 0/6
   * reads identically whether five people are mid-signature or five were never given a task,
   * and those are opposite findings. So the three states that are NOT the numerator are
   * printed under it: the split plus the numerator always sums to the denominator, and the
   * drill-down names the people.
   *
   * ⚠️ `draft` is IN the denominator. It used to be left out of `total` altogether, so a role
   * with 2 signed and 3 never-launched tasks rendered a green `✓ 2/2` and erased three people
   * from their own column. A task that was never launched cannot be signed — those people owe
   * this training exactly as much as the ones with no task at all.
   */
  const cellOf = (col) => {
    if (!col) return <td className="tm-st tm-st-na"><span className="tm-dash">—</span></td>;
    const n = { done: col.done, open: col.open, draft: col.draft || 0, miss: col.miss };
    const total = n.done + n.open + n.draft + n.miss;
    // Worst state present, read backwards through SPLIT — which is ordered worst-last, so no
    // task at all beats a task nobody can sign, which beats one simply not signed yet. An
    // empty column falls back to `done`, exactly as it did before.
    // ⚠️ `s.cls` is the CSS name (ok) and `s.k` the state name (done); they are deliberately
    // different words and ST is keyed on the second.
    const worst = [...SPLIT].reverse().find((s) => n[s.k] > 0) || SPLIT[0];
    const st = worst.cls;
    const glyph = ST[worst.k].glyph;
    const pctOf = (v) => (total ? (v / total) * 100 : 0);
    const bits = [];
    if (n.done) bits.push(`${n.done} signed`);
    if (n.open) bits.push(`${n.open} open`);
    if (n.draft) bits.push(`${n.draft} never launched — the task exists but was never sent out`);
    if (n.miss) bits.push(`${n.miss} with no task`);
    /* The quiz is a SECOND fraction, never folded into the first. Signed-of-expected and
       passed-of-eligible have different denominators — a person who hasn't signed yet is not
       counted as failing a quiz they were never allowed to sit — so merging them would make
       one number mean neither. Absent when this document has no published quiz. */
    const cq = col.quiz;
    const qTitle = cq && cq.expected
      ? ` · quiz: ${cq.passed} passed of ${cq.eligible} who can sit it`
        + (cq.locked ? `, ${cq.locked} still to sign` : '')
      : '';
    return (
      <td
        className={`tm-st tm-st-${st}`}
        title={`${bits.join(' · ')} — ${total} ${total === 1 ? 'person' : 'people'} in this role${qTitle}`}
      >
        <div className="tm-ci">
          <span className="tm-g">{glyph}</span>
          <span className="tm-fr">{n.done}/{total}</span>
        </div>
        {/* Four segments, not one fill. A single green fill said what fraction had signed and
            left the remainder as blank track — the same blank whether it is people mid-
            signature or people with nothing to sign. */}
        <div className="tm-bar">
          {SPLIT.filter((s) => n[s.k] > 0).map((s) => (
            <i key={s.k} className={`is-${s.cls}`} style={{ width: `${pctOf(n[s.k])}%` }} />
          ))}
        </div>
        {/* Everyone who is not in the numerator, by state. Absent when they all signed —
            "3/3" with nothing under it is the one case that needs no explaining. */}
        {total > n.done && (
          <div className="tm-sp">
            {SPLIT.filter((s) => s.k !== 'done' && n[s.k] > 0).map((s) => (
              <span key={s.k} className={`tm-sp-i is-${s.cls}`}>{n[s.k]} {s.word}</span>
            ))}
          </div>
        )}
        {cq && cq.expected > 0 && (
          <div className={`tm-cq ${cq.eligible && cq.passed >= cq.eligible ? 'is-ok' : ''}`}>
            <span className="tm-cq-g">🎓</span>{cq.passed}/{cq.eligible}
          </div>
        )}
      </td>
    );
  };

  /* The two "something is being hidden from you" notes. They live in the filter row, but they
     are the honesty mechanism, not part of the control — so they are rendered even when the row
     is collapsed. A document that silently disappears from a compliance panel is the one thing
     this screen must never do. */
  const undatedNote = undated > 0 ? (
    <button
      className="tm-count-more"
      onClick={() => { setPeriod('all'); setFrom(''); setTo(''); }}
      title="These documents have no readable approval date, so they can’t be placed in any period."
    >
      {undated} {undated === 1 ? 'document has' : 'documents have'} no approval date — clear the period
    </button>
  ) : null;
  /* `scopedAttn` / `scopedScored` are counted inside the current source (server-side), so the
     split never describes rows the grid is not showing. `unitWord` follows it too: calling a
     training record a "document" is the small inaccuracy that makes someone distrust the
     number beside it. */
  const unitWord = !formalAvailable || source === 'doc' ? 'documents'
    : source === 'formal' ? 'training records' : 'items';

  const hiddenNote = (q && hiddenByFilter > 0) ? (
    <button className="tm-count-more" onClick={() => setFilter('all')}>
      {hiddenByFilter} more match{hiddenByFilter === 1 ? 'es' : ''} but {hiddenByFilter === 1 ? 'is' : 'are'} hidden by this filter — show all
    </button>
  ) : null;

  return (
    <>
      {/* A page that could not be read. The grid keeps whatever it last drew underneath this
          rather than blanking: an empty table on a compliance panel reads as "no documents",
          which is a claim, where a stale page under a banner is plainly a stale page. */}
      {loadError && (
        <div className="tm-filters tm-filters-mini">
          <span className="tm-count">{loadError}</span>
        </div>
      )}
      {/* Collapsed by default — the ▽ beside the search box in the toolbar opens it. */}
      {!filtersOpen && (undatedNote || hiddenNote) && (
        <div className="tm-filters tm-filters-mini">
          <span className="tm-count">{undatedNote}{hiddenNote}</span>
        </div>
      )}
      {filtersOpen && (
      <div className="tm-filters">
        {/* The source narrows the population FIRST, so every state chip beside it counts within
            the source you picked. Rendered only when the panel actually has formal training —
            a switch with one populated side would read as "this account runs none". */}
        {formalAvailable && (
          <span className="tm-source" role="group" aria-label="Show">
            {Object.entries(SOURCE_LABEL).map(([k, label]) => (
              <button
                key={k}
                type="button"
                className={`tm-src ${source === k ? 'on' : ''}`}
                onClick={() => setSource(k)}
                title={{
                  all: 'Controlled documents and formal training records together',
                  doc: 'Read & understand training driven by a controlled document',
                  formal: 'Formal training records — courses and sessions, whose audience is roles and named trainees',
                }[k]}
              >
                {label}
                <span className="tm-chip-n">{sourceCounts[k] || 0}</span>
              </button>
            ))}
          </span>
        )}
        {Object.entries(FILTER_LABEL).map(([k, label]) => {
          /* The server's count, taken over the period-scoped population — so a chip that says
             12 yields 12 rows. With a search active it can read higher than the rows shown,
             which is what `hiddenNote` below exists to say out loud. */
          const n = counts[k] || 0;
          const quizOff = (k === 'quizgap' || k === 'hasquiz') && !quizInfo.on;
          const off = (!!attnBlocked && (k === 'noroles' || k === 'notappr')) || quizOff;
          return (
            <button
              key={k}
              type="button"
              className={`tm-chip ${filter === k ? 'on' : ''} ${off ? 'tm-chip-off' : ''}`}
              onClick={() => setFilter(k)}
              disabled={off}
              title={quizOff ? `${quizInfo.why} ${quizInfo.fix}`
                : off ? `${attnBlocked.why} ${attnBlocked.fix}`
                : k === 'quizgap'
                  ? 'Documents whose quiz is published and where somebody who has signed their task has not passed it'
                  : k === 'hasquiz'
                  ? 'Documents that have a quiz — published, still in draft, or filed under an earlier revision. The tag on each row says which'
                  : k === 'noroles'
                  ? 'Documents with a training record attached that name no role — nobody can ever be trained on them'
                  : k === 'notappr'
                    ? 'Documents that name roles but have no approved revision yet — nothing is released to train on'
                    : undefined}
            >
              {label}
              <span className="tm-chip-n">{n}</span>
            </button>
          );
        })}
        {/* Said in words, not only in a tooltip: two greyed-out chips read as a broken feature,
            and several browsers won't show a title on a disabled control. */}
        {attnBlocked && (
          <WhyBadge why={attnBlocked.why} fix={attnBlocked.fix} label="Roles" />
        )}
        {/* No badge for the quiz state: the "Has a quiz" chip carries its own count, so a 0
            beside a greyed-out chip already says "nobody has written one" — and its tooltip
            (`quizOff` above) still spells out why and what to do. A second warning pill for
            the same fact was pure noise. */}

        {/* Ranges over the document's approval date. Disabled rather than hidden when the DMS
            filter carries no approval-date column — a control that vanishes reads as a bug,
            and the caveat banner already names the column to add. */}
        <span className="tm-period">
          <select
            className="tm-period-sel"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            disabled={!!periodBlocked}
            title={periodBlocked
              ? `${periodBlocked.why} ${periodBlocked.fix}`
              : 'Show only documents approved in this period'}
            aria-label="Filter by approval period"
          >
            {PERIODS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
          {/* Said out loud, not only in a tooltip: a disabled control that just sits there greyed
              out reads as a broken feature, and several browsers won't show a title on one. */}
          {periodBlocked && (
            <WhyBadge why={periodBlocked.why} fix={periodBlocked.fix} label="Dates" />
          )}
          {period === 'custom' && (
            <>
              <input
                className="tm-period-date" type="date" value={from}
                onChange={(e) => setFrom(e.target.value)}
                aria-label="Approved from" title="Approved on or after"
              />
              <span className="tm-period-dash">→</span>
              <input
                className="tm-period-date" type="date" value={to}
                onChange={(e) => setTo(e.target.value)}
                aria-label="Approved to" title="Approved on or before"
              />
            </>
          )}
          {range && (
            /* The resolved range, spelled out — "last week" means different things to
               different people, and this is the only place that settles it. */
            <span className="tm-period-range">
              {range[0] || '…'} → {range[1] || '…'}
            </span>
          )}
        </span>

        {/* Column filter, not a row filter — it changes what you can SEE across, and the grid
            scrolls sideways, so with dozens of roles the columns that hold signatures are
            otherwise off-screen. Rendered only when it would actually hide something: a
            control that visibly does nothing reads as broken. */}
        {emptyRoleCount > 0 && (
          <label className="tm-rolefilter" title={
            `Hide the ${emptyRoleCount} role column${emptyRoleCount === 1 ? '' : 's'} no one in `
            + 'this panel holds. Rows, counts and totals are unchanged — only the columns.'}
          >
            <input type="checkbox" checked={peopledOnly} onChange={togglePeopledOnly} />
            Only roles with users
            <span className="tm-chip-n">{roleCols.length - emptyRoleCount}</span>
          </label>
        )}

        <span className="tm-count">
          {total === scopedTotal
            ? `${scopedTotal} ${unitWord}`
            : `${total} of ${scopedTotal} ${unitWord}`}
          {/* The two populations mean different things and the headline KPIs only ever count the
              scored ones, so the split is spelled out rather than left to be inferred from a
              total that grew. Counted within the current SOURCE, or the split would describe a
              population the grid is not showing. */}
          {scopedAttn > 0 && (
            <span className="tm-count-split">
              {scopedScored} scored · {scopedAttn} need attention
            </span>
          )}
          {/* The period drops undated documents by construction — say so on the toolbar, not
              only in the empty state, because the count is otherwise unexplained. */}
          {undatedNote}
          {/* Say it before the table looks empty, not only after. */}
          {hiddenNote}
        </span>
      </div>
      )}

      {/* The scorecards open and close with the filter row (the ▽ in the toolbar), so a closed
          row gives the grid the whole screen. They always show the whole panel. */}
      {filtersOpen && (
      <div className="tm-kpis">
        <div className="tm-kpi miss">
          <div className="tm-kpi-v">{funnel.missing}</div>
          <div className="tm-kpi-l">People with no task</div>
          <div className="tm-kpi-s">Hold a role the document names</div>
        </div>
        <div className="tm-kpi open">
          <div className="tm-kpi-v">{funnel.open}</div>
          <div className="tm-kpi-l">Open tasks</div>
          <div className="tm-kpi-s">Assigned, not yet signed</div>
        </div>
        <div className="tm-kpi ok">
          <div className="tm-kpi-v">{funnel.done}</div>
          <div className="tm-kpi-l">Signed</div>
          <div className="tm-kpi-s">Read &amp; understood</div>
        </div>
        <div className="tm-kpi doc">
          <div className="tm-kpi-v">{funnel.in_scope}</div>
          <div className="tm-kpi-l">Documents in scope</div>
          <div className="tm-kpi-s">Approved revision + roles set</div>
        </div>
        {/* Rendered only once a quiz exists. A fifth tile reading 0 of 0 on every account that
            has never written one would be a scorecard for a feature they are not using. The
            denominator is the people who CAN sit a quiz today — scoring against everyone who
            owes one would count people still waiting to sign as though they had refused. */}
        {qf && qf.expected > 0 && (
          <div className="tm-kpi quiz">
            <div className="tm-kpi-v">{qf.passed}<span className="tm-kpi-of">/{qf.eligible}</span></div>
            <div className="tm-kpi-l">Quizzes passed</div>
            <div className="tm-kpi-s">
              {qf.locked > 0 ? `${qf.locked} still waiting on a signature` : 'Of those who can sit one'}
            </div>
          </div>
        )}
      </div>
      )}

      <div className="tm-panel">
        <div className="tm-phead">
          <h2>Documents × role</h2>
          <DocsInfo funnel={funnel} qf={qf} />
          {pageRows.length > 0 && (
            <button
              type="button"
              className="tm-expand-all"
              onClick={toggleAll}
              title={allOpen ? 'Collapse every document' : 'Expand every document on this page'}
              aria-label={allOpen ? 'Collapse every document' : 'Expand every document on this page'}
              aria-pressed={allOpen}
            >
              {allOpen ? <UnfoldLessIcon /> : <UnfoldMoreIcon />}
              {allOpen ? 'Collapse all' : 'Expand all'}
            </button>
          )}
        </div>
        <div className="tm-gridwrap tm-gridwrap--sticky" ref={headBox}>
          <table className={`tm-grid${fit ? ' tm-grid--fit' : ''}`}>
            <thead>
              <tr>
                <th className="tm-doccol">Document</th>
                {visibleRoleCols.map((r) => (
                  <th className="tm-role" key={r} title={fit ? r : undefined}>
                    {fit ? <span className="tm-role-name">{r}</span> : r}
                    <span className="tm-role-n">
                      {peoplePerRole[r] || 0} {peoplePerRole[r] === 1 ? 'person' : 'people'}
                    </span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {pageRows.length === 0 && (
                <tr>
                  <td className="tm-empty" colSpan={visibleRoleCols.length + 1}>
                    {/* Before the first page lands there is nothing to say yet, and every branch
                        below is a CLAIM about the data — "no documents", "nothing in this period".
                        Making one of them while the answer is still in flight is how a panel
                        accuses an account of having no training for a few hundred milliseconds. */}
                    {!data ? (
                      <b>Loading…</b>
                    ) : range && inPeriodCount === 0 ? (
                      <>
                        <b>No document was approved in this period</b>
                        <span>
                          Nothing has an approval date between {range[0] || 'the beginning'} and{' '}
                          {range[1] || 'today'}.
                          {undated > 0 && ` ${undated} document${undated === 1 ? '' : 's'} ${undated === 1 ? 'has' : 'have'} no readable approval date and can’t be placed in a period at all.`}
                        </span>
                        <button
                          className="tm-empty-act"
                          onClick={() => { setPeriod('all'); setFrom(''); setTo(''); }}
                        >
                          Clear the period
                        </button>
                      </>
                    ) : q && searchHitCount === 0 ? (
                      <>
                        <b>No results found</b>
                        <span>
                          Nothing matches “{search.trim()}”. The search covers the document key
                          and name, and the people and roles inside it.
                        </span>
                      </>
                    ) : q ? (
                      <>
                        <b>No results found in “{FILTER_LABEL[filter]}”</b>
                        <span>
                          {hiddenByFilter === 1
                            ? '1 document matches '
                            : `${hiddenByFilter} documents match `}
                          “{search.trim()}”, but {{
                            miss: 'everyone who needs the training already has a task',
                            open: 'none of them has an open task',
                            quizgap: 'everyone who has signed has passed the quiz — or there is no published quiz on them',
                            hasquiz: 'none of them has a quiz',
                            noroles: 'all of them name at least one role',
                            notappr: 'all of them have an approved revision',
                          }[filter]} — so this filter hides
                          {hiddenByFilter === 1 ? ' it' : ' them'}.
                        </span>
                        <button className="tm-empty-act" onClick={() => setFilter('all')}>
                          Show all documents
                        </button>
                      </>
                    ) : filter !== 'all' ? (
                      <>
                        <b>
                          {{
                            miss: 'No document is missing a trainee',
                            open: 'No document has an open task',
                            quizgap: 'No quiz is outstanding',
                            hasquiz: 'No document here has a quiz',
                            noroles: 'Every document that owes training names its roles',
                            notappr: 'Every document that owes training is approved',
                          }[filter]}
                        </b>
                        <span>
                          {{
                            miss: 'Everyone who holds a role a document names has a training task.',
                            open: 'Every task that exists has been signed.',
                            quizgap: 'Everyone who has signed a task on a document with a published quiz has passed it. People who have not signed yet are counted by the two chips before this one — the quiz has not opened for them.',
                            hasquiz: 'Your account has quizzes, but none of them is filed under a document in this panel. A quiz belongs to the account and a panel is one filter over your library, so a quiz written for a document this panel does not cover will not appear here.',
                            noroles: 'Documents with a training record attached all name at least one role, so everyone who owes training can be identified.',
                            notappr: 'Every document with a training record attached has an approved revision to train on.',
                          }[filter]}
                        </span>
                        <button className="tm-empty-act" onClick={() => setFilter('all')}>
                          Show all documents
                        </button>
                      </>
                    ) : (
                      <>
                        <b>No documents in scope</b>
                        <span>
                          A document counts only when it has an approved revision and names at
                          least one role that a user profile holds.
                        </span>
                      </>
                    )}
                  </td>
                </tr>
              )}
              {/* The key carries the row's position as well as its document key. A duplicate
                  React key does not just warn — React keeps stale rows in the DOM when the list
                  changes, which showed as "the search returns documents that don't match".
                  The engine de-duplicates now, so this is belt and braces. */}
              {pageRows.map((d, di) => (
                <React.Fragment key={`${d.key}-${di}`}>
                  <tr
                    className={`tm-doc ${open[d.key] ? 'is-open' : ''} ${d.attn ? 'tm-doc-attn' : ''}`}
                    /* An attention row has nothing to expand — no cells, no revision history —
                       so it is not clickable and shows no chevron. A row that opens onto
                       nothing reads as data that failed to load. */
                    onClick={d.attn ? undefined : () => setOpen((o) => ({ ...o, [d.key]: !o[d.key] }))}
                  >
                    <td>
                      <div className="tm-dc">
                        <span className="tm-chev">{d.attn ? '' : '▶'}</span>
                        {/* The key stays plain text: clicking anywhere on the row — key
                            included — expands it, which is the common action. Opening the
                            document in Orcanos is the separate icon on the right. */}
                        <span className="tm-dk">{d.key}</span>
                        <span className="tm-dn">{d.name}</span>
                        {/* Why this document can't be scored — never a bare empty row, which
                            would read as 0 % trained rather than "not measurable". */}
                        {/* Which population the row is in. Without it a training record reads as
                            a document with a missing revision and a missing approval date,
                            rather than as an item those two columns do not apply to. */}
                        {d.src === 'formal' && (
                          <span
                            className="tm-srctag"
                            title="A formal training record — a course or session. Its audience is the roles it names plus anyone named individually, and it is signed off in its own right."
                          >
                            Formal training
                          </span>
                        )}
                        {d.attn && (
                          <span
                            className={`tm-attn tm-attn-${d.attn === 'not_approved' ? 'notappr' : 'noroles'}`}
                            title={d.attn === 'no_audience'
                              ? (d.src === 'formal'
                                ? 'This training record names neither a role nor a trainee, so nobody can ever be trained on it — and Orcanos reports nothing about it.'
                                : 'This document has a training record attached but names neither a role nor a trainee, so nobody can ever be trained on it — and Orcanos reports nothing about it.')
                              : d.attn === 'no_roles'
                                ? 'This document has a training record attached but names no role, so nobody can ever be trained on it — and Orcanos reports nothing about it. (Its filter selects no Trainees column, so named individuals could not be checked.)'
                                : `This document names ${d.nroles === 1 ? 'a role' : d.nroles > 1 ? `${d.nroles} roles` : `${(d.trainees || []).length} named ${(d.trainees || []).length === 1 ? 'trainee' : 'trainees'}`} but has no approved revision, so there is no released version to train on.`}
                          >
                            {ATTN_LABEL[d.attn]}
                          </span>
                        )}
                        {/* What a formal record carries instead of a revision — Category and
                            Department are on the sub-line (`DocMetaLine`); they describe the
                            record rather than say how its training is going. */}
                        {/* The record's OWN sign-off, printed verbatim like every other date
                            here. It is signed independently of its tasks, which is why it is
                            never allowed to promote a person's cell below. */}
                        {d.src === 'formal' && d.completed_on && (
                          <span
                            className="tm-appr"
                            title={`This training record was signed off as complete on ${d.completed_on}. It is signed independently of its tasks.`}
                          >
                            Completed {d.completed_on}
                          </span>
                        )}
                        {d.attn && d.roles && d.roles.length > 0 && (
                          <span className="tm-attn-rolelist" title={d.roles.join(', ')}>
                            {d.roles.join(', ')}
                            {d.people > 0 && ` · ${d.people} ${d.people === 1 ? 'person' : 'people'} would owe it`}
                          </span>
                        )}
                        {d.attn && d.state && (
                          <span className="tm-attn-state" title="The document’s routing state in Orcanos">
                            {d.state}
                          </span>
                        )}
                        {/* Absent on an unapproved document — "Rev " alone reads as missing data. */}
                        {d.rev && (
                          <span className="tm-rev" title="The last approved revision — the one the audience owes training on">
                            Rev {d.rev}
                          </span>
                        )}
                        {/* The document's own score, before any of the per-role cells. It sits
                            here rather than in the columns because it must not depend on how
                            wide the window is: the cells scroll sideways, this does not. */}
                        <DocRollup cells={d.cells} />
                        {/* The approval date is printed VERBATIM, exactly as the tenant renders
                            it. A filter result reads day-first and an item read through
                            QW_Get_Object reads month-first, and one report consumes both —
                            reformatting would move a date by up to eleven months on one of
                            them. The period filter compares `approved_iso` instead. */}
                        {/* A formal record's `approved_on` is an ALIAS of its completion date,
                            carried so one period control can range over both sources. It is
                            printed above under its own name — never here, where the word
                            "Approved" would make one value mean two things. */}
                        {/* ⚠️ The date is the DOCUMENT's routing END date, not the revision's own.
                            On a document whose later revision is still in routing they are the
                            same thing — that routing hasn't ended, so the last end date is the
                            approved revision's. But once that later routing FINISHES without
                            approving (cancelled, rejected), the end date becomes ITS date while
                            the approved revision stays put. Observed on orca60: DMS-11599 shows
                            "Rev A · Approved 19/07/2016", and 19/07/2016 is when revision D's
                            routing was cancelled — rev A was approved in 2014.

                            We cannot tell the two apart from the report alone (the revision's own
                            date is in the Revisions tab, one API call per document — Note #17), so
                            the tooltip states the condition instead of asserting the wrong one. */}
                        {d.src !== 'formal' && d.approved_on && (
                          <span
                            className="tm-appr"
                            title={d.rev_current
                              ? `${d.approved_on} is the routing end date recorded on the document. `
                                + `That is revision ${d.rev}'s approval date unless revision `
                                + `${d.rev_current}'s routing has since ended — on a cancelled or `
                                + `rejected routing this date belongs to that one instead.`
                              : `Revision ${d.rev} was approved on ${d.approved_on}`}
                          >
                            Approved {d.approved_on}
                          </span>
                        )}
                        {/* `Current rev — in routing`, `+N prev` and `+N roles with no user` are
                            on the sub-line the row opens onto (`DocMetaLine`). The line itself
                            carries only what identifies the document and how far along it is. */}
                        {/* ------------------------------------------------ the quiz zone
                            The quiz is a different KIND of fact from everything to its left.
                            Rev, Approved, Category, Department describe the document; the quiz
                            is a thing you open and act on — and it is the one tag here that is
                            a link. Mixed into the same chip run it read as one more variable
                            and was routinely missed, so it is pulled to the right of the row
                            behind a divider and given a heavier chip. Same tags, same rules —
                            only the position and the weight changed. */}
                        {(d.quiz || d.quiz_stale) && (
                          <span className="tm-qzone">
                            {/* The quiz attached to THIS revision. `passed of eligible`, because
                                somebody who hasn't signed yet can't have sat it — see the cell. */}
                            {d.quiz && d.quiz.status === 'published' && (
                              <QuizTag
                                cls={d.quiz.eligible && d.quiz.passed >= d.quiz.eligible ? 'is-ok' : ''}
                                onOpen={onOpenQuiz ? () => onOpenQuiz(d.quiz.id, 'results') : null}
                                title={`“${d.quiz.title}” — ${d.quiz.questions} question${d.quiz.questions === 1 ? '' : 's'}, `
                                  + `pass mark ${d.quiz.pass_mark}%. ${d.quiz.passed} of ${d.quiz.eligible} `
                                  + `who have signed have passed it`
                                  + (d.quiz.locked ? `; ${d.quiz.locked} have still to sign, so the quiz has not opened for them.` : '.')}
                              >
                                <span className="tm-qtag-i" aria-hidden="true">🎓</span>
                                Quiz {d.quiz.passed}/{d.quiz.eligible}
                              </QuizTag>
                            )}
                            {d.quiz && d.quiz.status === 'draft' && (
                              <QuizTag
                                cls="is-draft"
                                /* Straight to the questions — an unfinished draft is something to
                                   go on writing, not a result to read. */
                                onOpen={onOpenQuiz ? () => onOpenQuiz(d.quiz.id, 'questions') : null}
                                title="A quiz is being written for this revision. A draft is offered to nobody — publish it to put it in front of the people below."
                              >
                                <span className="tm-qtag-i" aria-hidden="true">🎓</span>
                                Quiz draft
                              </QuizTag>
                            )}
                            {/* Published, and reaching nobody. A quiz is filed under a revision;
                                when the document moves on it silently stops matching every trainee
                                while still reading "Published" on the quiz list. */}
                            {d.quiz_stale && d.quiz_stale.status === 'published' && (
                              <QuizTag
                                cls="is-stale"
                                /* The fix for a stranded quiz is “Start next revision”, which lives
                                   on the quiz itself — so this is the one chip whose click is an
                                   action rather than a look. */
                                onOpen={onOpenQuiz ? () => onOpenQuiz(d.quiz_stale.id, 'questions') : null}
                                title={`A published quiz exists for ${d.key}, but it is filed under revision `
                                  + `${d.quiz_stale.revision || '—'} and this document is now on `
                                  + `${d.quiz_stale.doc_revision || '—'}. Trainees owe the current revision, so it is `
                                  + `reaching nobody — carry it forward with “Start next revision”.`}
                              >
                                <span className="tm-qtag-i" aria-hidden="true">🎓</span>
                                Quiz on rev {d.quiz_stale.revision || '—'} — reaching nobody
                              </QuizTag>
                            )}
                          </span>
                        )}
                        {d.url && (
                          /* stopPropagation, or opening Orcanos would also toggle the row. */
                          <a
                            className="tm-open"
                            href={d.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`Open ${d.key} in Orcanos`}
                            aria-label={`Open ${d.key} in Orcanos`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg viewBox="0 0 16 16" aria-hidden="true">
                              <path d="M6.5 2.5h-3a1 1 0 0 0-1 1v9a1 1 0 0 0 1 1h9a1 1 0 0 0 1-1v-3" />
                              <path d="M9.5 2.5h4v4" />
                              <path d="M13.5 2.5 7 9" />
                            </svg>
                          </a>
                        )}
                      </div>
                    </td>
                    {visibleRoleCols.map((r) => (
                      <React.Fragment key={r}>{cellOf(d.cols[r])}</React.Fragment>
                    ))}
                  </tr>
                  {/* The row's own chips, above the people — they describe the document, not any
                      one person. An attention row is excluded because it cannot be opened at
                      all, which is why its tags stayed on the line. */}
                  {open[d.key] && !d.attn && (d.cells || []).length > 0 && (
                    <DocMetaLine d={d} span={visibleRoleCols.length + 1} />
                  )}
                  {/* The expansion is scoped to the CURRENT revision. Signed on revision A while
                      C is released is not trained in audit terms — the older signatures live
                      under "Previous revisions" below, where they belong. */}
                  {open[d.key] && d.cells.map((c, i) => (
                    <PersonRow
                      key={`${d.key}-${i}`}
                      name={c.user.name}
                      role={c.role}
                      st={c.st}
                      docRev={d.rev_major}
                      quiz={c.q}
                      span={visibleRoleCols.length + 1}
                      task={c.t ? {
                        tk: c.t.key, turl: c.t.url, ts: c.t.routing, due: c.t.due, rev: c.t.rev,
                        sd: c.t.signed, ad: c.t.assigned,
                      } : null}
                      onCertify={() => setCert({
                        person: { name: c.user.name, uid: c.user.uid, role: c.role },
                        doc: { key: d.key, name: d.name, rev: d.rev, url: d.url },
                        task: {
                          tk: c.t && c.t.key, turl: c.t && c.t.url, ts: c.t && c.t.routing,
                          due: c.t && c.t.due, rev: c.t && c.t.rev,
                          sd: c.t && c.t.signed, ad: c.t && c.t.assigned,
                        },
                      })}
                    />
                  ))}
                  {/* Absent on a snapshot built before v3.10.0 — RevisionHistory renders
                      nothing rather than claiming the document has no history. */}
                  {open[d.key] && (
                    <RevisionHistory
                      doc={d}
                      span={visibleRoleCols.length + 1}
                      onOpenQuiz={onOpenQuiz}
                      onCertify={(person, task) => setCert({
                        person,
                        doc: { key: d.key, name: d.name, rev: d.rev, url: d.url },
                        task,
                      })}
                    />
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
        <div className="tm-foot">
          <div className="tm-paging">
            <select
              className="tm-rows-select"
              value={pageSize}
              onChange={(e) => changePageSize(parseInt(e.target.value, 10))}
              aria-label="Documents per page"
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} per page</option>)}
            </select>
            <button disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>← Prev</button>
            <span className="tm-pageno">Page {safePage} of {totalPages}</span>
            <button disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>Next →</button>
            <span className="tm-showing">
              Showing {pageRows.length ? start + 1 : 0}–
              {start + pageRows.length} of {total}
            </span>
          </div>
          <div className="tm-legend">
            <span className="tm-lg"><span className="tm-g tm-g-ok">✓</span> Signed</span>
            <span className="tm-lg" title="Assigned, not signed yet"><span className="tm-g tm-g-open">◷</span> In work</span>
            <span className="tm-lg" title="The task exists but was never sent out"><span className="tm-g tm-g-draft">·</span> Not launched</span>
            <span className="tm-lg" title="No training task exists"><span className="tm-g tm-g-miss">?</span> No task</span>
            {qf && qf.expected > 0 && (
              <span className="tm-lg" title="Quiz passed / who can sit it"><span className="tm-cq-g">🎓</span> Quiz</span>
            )}
          </div>
        </div>
      </div>

      {cert && (
        <TrainingCertificate
          person={cert.person}
          single={{ doc: cert.doc, task: cert.task }}
          setupId={setupId}
          panelName={panelName}
          builtAt={builtAt}
          onClose={() => setCert(null)}
        />
      )}
    </>
  );
}

/* ------------------------------------------------------------------ funnel */

function FunnelView({ report }) {
  const f = report.funnel;
  const noProfile = (f.recon || []).find((r) => r[0] === 'User has no User Profile');

  // The prose — every stage's hint and the "Read this way" key — is OFF by default: with all of
  // it on, the picture is buried in text. The ℹ button in the header brings it back. See the
  // `chrome.foots` note in components/FunnelView.
  const [showInfo, setShowInfo] = useState(false);

  /**
   * The same picture the trace matrix's coverage funnel draws, over this snapshot: THREE stages —
   * Documents → Training tasks expected → Tasks created — green carrying on to the next, red
   * leaking out of it. Built here from the cached report, so an existing snapshot renders it with
   * no engine change and no rebuild.
   *
   * **Each stage keeps its own bar.** Folding "Tasks created" into the expected bar as a green
   * segment loses the number people actually quote — how many training tasks exist in Orcanos —
   * and the funnel stops tapering, which is the whole point of drawing one.
   *
   * A stage's TOTAL is the population that reached it and its segments are what became of that
   * population, so the green of one bar is the total of the next.
   *
   * **There is no separate "Signed" bar (v3.25.1).** Signed is already a segment of *Tasks
   * created* — the same number twice, once as a share of the tasks that exist and once as a share
   * of the tasks owed — and a whole bar that is one whole segment reads as an outcome the funnel
   * has arrived at rather than a population that survived. The figure that bar carried (signed as
   * a share of what the account *owes*, not of what exists) is the one thing it said that the
   * segment does not, so it moves onto that stage's foot rather than being lost.
   *
   * The hints stay on the bars they describe — they are the point of the view: a drop at each
   * stage has a different owner.
   */
  const funnel = useMemo(() => {
    // Documents that owe training and can't be scored (no roles / not approved). Only counted
    // when the Training List column exists — otherwise the engine cannot tell a document that
    // owes training from one that needs none, and a confident 0 would be a claim, not a fact
    // (the same three-state signal the Matrix chips read; Critical Note #24).
    const attnKnown = !!(f.attention && f.training_list_field);
    const attn = attnKnown ? (f.attention.total || 0) : 0;
    const notSignable = Math.max(0, (f.created || 0) - (f.done || 0) - (f.open || 0));

    const stages = [
      {
        id: 'docs',
        name: 'Documents',
        unit: 'documents owe training',
        total: (f.in_scope || 0) + attn,
        segments: [
          // "Scored" only claims as much as the snapshot can know: without the Training List
          // column there is nothing to be scored *against*, so the green is just what is in scope.
          { key: 'scope', label: attnKnown ? 'Scored' : 'In scope', kind: 'good', count: f.in_scope || 0 },
          { key: 'attn', label: 'Can’t be scored', kind: 'bad', count: attn },
        ],
        foot_kind: 'note',
        foot: attnKnown
          ? (attn > 0
            ? <><span className="tm-tag t-cfg">Configuration</span><b>{attn}</b> name no role, or
              have no approved revision — nobody can be trained on them.</>
            : <>Every document that owes training names a role and has an approved revision.</>)
          : <>Of {f.dms_total} in the library, these have an approved revision and name Roles.</>,
      },
      {
        id: 'expected',
        name: 'Training tasks expected',
        unit: 'tasks should exist',
        total: f.expected || 0,
        segments: [
          { key: 'created', label: 'Created', kind: 'good', count: f.created || 0 },
          { key: 'missing', label: 'Never assigned', kind: 'bad', count: f.missing || 0 },
        ],
        foot_kind: 'note',
        foot: <><span className="tm-tag t-role">Role resolution</span><b>{f.missing}</b> people
          were owed training and never assigned — Orcanos can only count the tasks that exist.</>,
      },
      {
        id: 'created',
        name: 'Tasks created',
        unit: 'exist in Orcanos',
        total: f.created || 0,
        segments: [
          { key: 'done', label: 'Signed', kind: 'good', count: f.done || 0 },
          { key: 'open', label: 'Not signed', kind: 'warn', count: f.open || 0 },
          { key: 'draft', label: 'Draft', kind: 'warn', count: notSignable },
        ],
        foot_kind: 'note',
        // The segment above is a share of the tasks that EXIST; this says the same signature
        // count as a share of the training the account OWES — the only figure the old fourth
        // bar carried that the segment does not.
        foot: <><span className="tm-tag t-people">People</span><b>{f.done}</b> of the {f.expected} owed
          are signed · {f.open} assigned and not yet
          signed{notSignable > 0 ? `, ${notSignable} never launched` : ''}.</>,
      },
    ];

    /* ---- the quiz leg — TWO stages, because two different people own them (v3.25.1) ----
     *
     * A quiz is written per DOCUMENT and owed per PERSON, so "60 documents have no quiz" hides
     * the size of the hole: one document with 40 trainees is a bigger gap than ten with two.
     * The obligation is therefore counted the same way training is — one per person, per
     * document — and split across two bars, because the two drops have different owners:
     *
     *   Quizzes needed  — every obligation in scope. Green: a quiz exists to sit.
     *                     Red: NOT CREATED — nobody has written one. An AUTHORING gap.
     *   Quizzes         — the obligations that have a quiz. What became of them. A
     *                     COMPREHENSION gap (and, for `locked`, a signature that is missing).
     *
     * The funnel's own grammar does the rest: the green of the first bar is the total of the
     * second, so the taper is real and neither number has to be inferred from the other.
     *
     * `locked` gets its OWN segment rather than being dropped: the quiz opens only once the
     * task is signed (`quiz_engine.SIT_STATES`), so those people were never allowed to sit it
     * and must never read as a comprehension failure — but they are part of the population
     * this bar covers, so leaving them out silently would make the segments not add up. The
     * pass rate that is quoted anywhere else stays `passed of ELIGIBLE`, and the foot says so
     * in words (Critical Note #29).
     *
     * Rendered only when a quiz exists at all. Two empty bars on every account that has never
     * written one would make the funnel taper to zero and read as a total failure.
     */
    const q = f.quiz;
    if (q && q.configured && (q.expected > 0 || q.quizzes > 0)) {
      const covered = q.eligible || 0;
      // "No attempts left" is a failure that has run out of road, not a fifth outcome — it is
      // folded into Failed so the bar reads as the states a person can actually be in, and
      // named separately in the foot because it is the one an author has to act on.
      const failed = (q.failed || 0) + (q.exhausted || 0);
      // `needed` is absent on a report rendered by an older build; fall back to what the two
      // halves add up to rather than showing a total that contradicts its own segments.
      const noQuiz = q.no_quiz || 0;
      const needed = q.needed || ((q.expected || 0) + noQuiz);

      stages.push({
        id: 'quiz_needed',
        name: 'Quizzes needed',
        unit: 'owed — one per person, per document',
        total: needed,
        segments: [
          { key: 'written', label: 'Quiz written', kind: 'good', count: q.expected || 0 },
          { key: 'none', label: 'Not created', kind: 'bad', count: noQuiz },
        ],
        foot_kind: 'note',
        foot: (
          <>
            <span className="tm-tag t-cfg">Authoring</span>
            <b>{q.published || 0}</b> published {q.published === 1 ? 'quiz' : 'quizzes'} cover{' '}
            {q.docs_with_quiz || 0} {(q.docs_with_quiz || 0) === 1 ? 'document' : 'documents'}.
            {q.docs_no_quiz > 0 && <> <b>{q.docs_no_quiz}</b> documents in scope have no quiz at
              all — that is {noQuiz} {noQuiz === 1 ? 'person' : 'people'} with nothing to sit.</>}
            {q.no_quiz_draft > 0 && <> {q.no_quiz_draft} of them are waiting on a quiz that is
              still a draft.</>}
          </>
        ),
      });

      stages.push({
        id: 'quiz',
        name: 'Quizzes',
        unit: `to sit, from ${q.published || 0} published ${q.published === 1 ? 'quiz' : 'quizzes'}`,
        total: q.expected || 0,
        segments: [
          { key: 'passed', label: 'Completed', kind: 'good', count: q.passed || 0 },
          { key: 'in_progress', label: 'In progress', kind: 'warn', count: q.in_progress || 0 },
          { key: 'not_started', label: 'Not started', kind: 'warn', count: q.not_started || 0 },
          { key: 'failed', label: 'Failed', kind: 'bad', count: failed },
          // Not a quiz failure — a missing signature, already counted as one on "Tasks
          // created". Drawn so the bar adds up, labelled so it can never read as the other.
          { key: 'locked', label: 'Task not signed yet', kind: 'warn', count: q.locked || 0 },
        ],
        foot_kind: 'note',
        foot: (
          <>
            <span className="tm-tag t-people">Comprehension</span>
            <b>{q.passed || 0}</b> of {covered} who can sit one have passed.
            {q.exhausted > 0 && <> {q.exhausted} have no attempts left.</>}
            {q.locked > 0 && <> {q.locked} more owe one but haven’t signed their training task
              yet, so it hasn’t opened for them — that is a signature, not a quiz result.</>}
          </>
        ),
      });
    }

    return {
      modes: { level: { stages, drill: {} } },
      kpis: {},
      // Off: the two count modes and the drill-down are the trace matrix's questions, and the
      // Matrix tab already carries this panel's scorecards.
      chrome: {
        modes: false,
        kpis: false,
        drill: false,
        foots: showInfo,
        legend: [
          { kind: 'good', label: 'Carries on to the next stage' },
          { kind: 'bad', label: 'Leaks here — a finding' },
          { kind: 'warn', label: 'Assigned, not yet done' },
        ],
      },
    };
  }, [f, showInfo]);

  return (
    <>
      <div className="tm-panel">
        <div className="tm-phead">
          <h2>Where training leaks</h2>
          <span className="tm-pnote">Each stage fails for a different reason — and has a different owner</span>
          <button
            type="button"
            className={`tm-info-toggle${showInfo ? ' on' : ''}`}
            onClick={() => setShowInfo((v) => !v)}
            aria-pressed={showInfo}
            title={showInfo ? 'Hide the explanations' : 'Explain what each stage means'}
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
              strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" />
              <line x1="12" y1="8" x2="12.01" y2="8" />
            </svg>
            {showInfo ? 'Hide info' : 'Info'}
          </button>
        </div>
        <div className="tm-fn">
          <div className="tm-pre">
            <span>Before any person is even considered:</span>
            <b>{f.dms_total}</b><span>documents →</span>
            {/* A filter that returns a document twice would otherwise expect its trainees
                twice. Say how many rows were collapsed rather than quietly dropping them. */}
            {f.dms_duplicates > 0 && (
              <span className="tm-leak" title="The DMS filter returned the same document more than once; each document is counted once.">
                · {f.dms_duplicates} duplicate {f.dms_duplicates === 1 ? 'row' : 'rows'} collapsed
              </span>
            )}
            <b>{f.dms_approved}</b><span>have an approved revision →</span>
            {/* The audience is Roles ∪ Trainees — a document released to named people with no
                role set is trained, and counting roles alone read it as naming nobody. */}
            <b>{f.in_scope}</b><span>also name an audience</span>
            <span className="tm-leak">· {f.dms_approved - f.in_scope} approved documents name nobody at all</span>
          </div>

          <CoverageFunnel funnel={funnel} />

          {showInfo && <div className="tm-pre tm-pre-last">
            <span className="tm-tag t-cfg">Read this way</span>
            <span>
              Red in the first stage is a <b>configuration</b> problem — roles missing from a
              document, or a revision never approved. Red in the second is <b>automation</b> —
              the task was never created. Amber in the third is <b>people</b> — the task exists
              and nobody signed it.
              {f.quiz && f.quiz.configured && f.quiz.expected > 0 && (
                <> Red in the last is <b>comprehension</b> — the document was signed for and the
                  quiz that checks it was not passed. One number, four different meanings.</>
              )}
              {!(f.quiz && f.quiz.configured && f.quiz.expected > 0) && ' One number, three different meanings.'}
            </span>
          </div>}
        </div>
      </div>

      {/* What the quiz leg cannot see. Every item here is a quiz result that exists and is
          counted by nobody — the same class of finding as the signed-task reconciliation
          above, and it hides the same way: silently, while everything reads as fine. */}
      {f.quiz && f.quiz.configured && (
        <div className="tm-panel">
          <div className="tm-phead">
            <h2>Quizzes</h2>
            <span className="tm-pnote">Comprehension is a second question — and it fails quietly</span>
          </div>
          <div className="tm-revsum">
            <p>
              <b>{f.quiz.published}</b> published{' '}
              {f.quiz.published === 1 ? 'quiz covers' : 'quizzes cover'}{' '}
              <b>{f.quiz.docs_with_quiz}</b>{' '}
              {f.quiz.docs_with_quiz === 1 ? 'document' : 'documents'} in scope
              {f.quiz.drafts > 0 && <>, and {f.quiz.drafts} {f.quiz.drafts === 1 ? 'is a draft' : 'are drafts'} nobody is being offered</>}.
            </p>
            <div className="tm-revstats">
              <span className="tm-pill p-ok">{f.quiz.passed} passed</span>
              <span className="tm-pill p-open">{(f.quiz.failed || 0) + (f.quiz.in_progress || 0)} attempted, not passed</span>
              <span className="tm-pill p-miss">{f.quiz.not_started} never started</span>
              {f.quiz.exhausted > 0 && (
                <span className="tm-pill p-miss">{f.quiz.exhausted} out of attempts</span>
              )}
              {f.quiz.locked > 0 && (
                <span className="tm-pill p-na" title="These people owe a quiz but have not signed their training task, so it has not opened for them.">
                  {f.quiz.locked} waiting on a signature
                </span>
              )}
            </div>
            <p className="tm-muted">
              A quiz result never changes a training state. Signing the task is the training;
              the quiz checks what was understood, and the two are carried side by side
              precisely so they can disagree.
            </p>

            {f.quiz.docs_no_quiz > 0 && (
              <p className="tm-muted">
                <b>{f.quiz.docs_no_quiz}</b>{' '}
                {f.quiz.docs_no_quiz === 1 ? 'document has' : 'documents have'} no quiz at all —
                their trainees are counted in every stage above this one and in none of this one.
              </p>
            )}

            {/* Published, filed under a revision the report no longer calls current: offered to
                nobody, while the quiz list still shows it as Published with a healthy 0 of 0. */}
            {f.quiz.stale_total > 0 && (
              <p className="tm-revwarn">
                <b>{f.quiz.stale_total} published{' '}
                {f.quiz.stale_total === 1 ? 'quiz is' : 'quizzes are'} reaching nobody.</b>{' '}
                They are filed under a revision this report no longer calls current, so they
                match no trainee at all:{' '}
                {(f.quiz.stale || []).map((s) => `${s.doc_key} rev ${s.revision || '—'}`).join(', ')}.
                {' '}Carry each one forward with <b>Start next revision</b> and publish that.
              </p>
            )}

            {/* The identity trap of Critical Note #24, one layer up: the Orcanos login name a
                quiz is sat under is not necessarily the `Assigned To` the matrix is keyed on. */}
            {f.quiz.unmatched_total > 0 && (
              <p className="tm-revwarn">
                <b>{f.quiz.unmatched_total} quiz{f.quiz.unmatched_total === 1 ? '' : 'zes'} were
                sat by somebody this report has no cell for</b>, so those results are counted
                nowhere above:{' '}
                {(f.quiz.unmatched || []).map((u) => `${u.uid} (${u.attempts})`).join(', ')}.
                {' '}A quiz is sat under the Orcanos login name; the matrix is keyed on the user
                profile’s <b>Assigned To</b>. Nothing is matched on a resemblance — correct one
                of the two in Orcanos and rebuild.
              </p>
            )}

            {f.quiz.pending_writeback > 0 && (
              <p className="tm-revwarn">
                <b>{f.quiz.pending_writeback}{' '}
                {f.quiz.pending_writeback === 1 ? 'pass has' : 'passes have'} not been written
                back to Orcanos.</b> They are recorded here and are shown on the people who
                earned them, but no training record carries them yet.
              </p>
            )}
          </div>
        </div>
      )}

      {f.revisions && f.revisions.revisions > 0 && (
        <div className="tm-panel">
          <div className="tm-phead">
            <h2>Revision history</h2>
            <span className="tm-pnote">
              Everything above judges the <b>current</b> revision only — this is what happened before it
            </span>
          </div>
          <div className="tm-revsum">
            <p>
              <b>{f.revisions.revisions}</b> superseded{' '}
              {f.revisions.revisions === 1 ? 'revision' : 'revisions'} across{' '}
              <b>{f.revisions.docs_with_history}</b>{' '}
              {f.revisions.docs_with_history === 1 ? 'document' : 'documents'} still carry training
              tasks. Open a document in the Matrix and expand <b>Previous revisions</b> to see them.
            </p>
            <div className="tm-revstats">
              <span className="tm-pill p-ok">{f.revisions.done} signed</span>
              <span className="tm-pill p-open">{f.revisions.open} open</span>
              <span className="tm-pill p-miss">{f.revisions.missing} never trained</span>
              <span className="tm-pill p-na">{f.revisions.not_yet_joined} not yet joined</span>
              {f.revisions.unknown > 0 && (
                <span className="tm-pill p-unk">{f.revisions.unknown} unknown</span>
              )}
            </div>
            <p className="tm-muted">
              A person who joined <b>after</b> a revision was released never owed its training, so
              they are counted as <b>not yet joined</b> rather than missing. The current revision
              is deliberately not date-gated — whoever holds the role today owes today’s revision,
              however recently they arrived.
            </p>
            {f.revisions.rule === 'unavailable' && (
              <p className="tm-revwarn">
                <b>The date rule could not run.</b> No joining or created date could be read for
                the user profiles, so a person who was never here cannot be told apart from one who
                was skipped — those rows read <b>unknown</b>. Add a <b>Created Date</b> (or Joining
                Date) column to the User Profile filter and rebuild.
              </p>
            )}
            {f.revisions.task_date_field == null && (
              <p className="tm-muted">
                Each revision is dated by the earliest <b>due date</b> of its tasks (shown with
                “≈”), because the Training Task filter selects no Created Date column. A due date
                is later than the release, so a person who joined in between can read as owing
                training they never did. Add <b>Created Date</b> to that filter for an exact answer.
              </p>
            )}
            {(f.revisions.user_date_ambiguous || f.revisions.task_date_ambiguous) && (
              <p className="tm-muted">
                Every date in this account fits both <code>DD/MM</code> and <code>MM/DD</code>, so
                the order could not be proven from the data; it was read as{' '}
                <code>{f.revisions.user_date_order === 'mdy' ? 'MM/DD/YYYY' : 'DD/MM/YYYY'}</code>.
              </p>
            )}
            {f.revisions.revisions_dropped > 0 && (
              <p className="tm-muted">
                {f.revisions.revisions_dropped} older revisions beyond the most recent 12 per
                document are not shown.
              </p>
            )}
          </div>
        </div>
      )}

      <div className="tm-warn">
        <b>Where the {f.signed_total} signed tasks went</b>
        <p className="tm-warn-sub">
          This account has {f.signed_total} tasks with routing state <b>Approved</b>, but only{' '}
          <b>{f.done}</b> appear in the matrix. Every one is accounted for:
        </p>
        <table className="tm-recon">
          <tbody>
            {(f.recon || []).map(([label, n]) => (
              <tr key={label}>
                <td className={`tm-recon-n ${label === 'Counted in the matrix' ? 'is-ok' : ''}`}>{n}</td>
                <td>{label}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <b>Data-quality findings this run surfaced</b>
        <ul>
          <li>
            <b>The User Profile filter returns only {f.users} people</b> — but at least{' '}
            {f.users + (f.ghosts || []).length} hold training tasks. The matrix can only ever see
            someone who has a User Profile, so <b>{noProfile ? noProfile[1] : 0} signed tasks are
            invisible by construction.</b> Widening that filter is the single highest-impact fix.
          </li>
          <li>
            <b>{f.roles_on_docs - f.roles_with_users} of {f.roles_on_docs} roles</b> named on
            documents have <b>no user profile at all</b> — training for them can never be
            assigned or measured
            {(f.roles_no_users || []).length > 0 && <>: {f.roles_no_users.join(', ')}</>}.
          </li>
          <li>
            <b>{f.unparsed} of {f.tasks_total} training tasks</b> don't follow the Read &amp;
            Understand naming pattern — most likely formal-training tasks, currently unlinkable
            to a document.
          </li>
          {(f.ghosts || []).length > 0 && (
            <li>
              <b>{f.ghosts.length} user IDs hold tasks but match no User Profile</b>:{' '}
              {f.ghosts.map((g) => `${g[0]} (${g[1]})`).join(', ')}. Tasks are matched to a
              person by the user ID in the task name against the profile's <b>Assigned To</b>,
              never by display name.
            </li>
          )}
          {(f.ghost_matches || []).length > 0 && (
            <li className="tm-nearmiss">
              <b>Probable identity mismatch — fix this first.</b>
              <ul>
                {f.ghost_matches.map((g) => (
                  <li key={g.id}>
                    <b>{g.count}</b> tasks are named for <code>{g.id}</code>, which is not any
                    profile's <b>Assigned To</b> — but it looks like{' '}
                    {g.suggests.map((p) => `${p.name} (Assigned To: ${p.uid})`).join(', ')}.
                    {' '}Those tasks are <b>not</b> counted, so that person can read as untrained.
                    Nothing is matched on a resemblance: correct <b>Assigned To</b> in Orcanos,
                    or the task names, and rebuild.
                  </li>
                ))}
              </ul>
            </li>
          )}
          {(f.no_role_users || []).length > 0 && (
            <li>
              <b>{f.no_role_users.length} user profile(s) carry no Job Title</b>:{' '}
              {f.no_role_users.join(', ')} — they match no document.
            </li>
          )}
        </ul>
      </div>
    </>
  );
}

/* ----------------------------------------------------------------- reports */

const REPORT_PAGE_SIZE_KEY = 'trainingReportRowsPerPage';

/** What each reports tab is a list OF — used by the pager's labels. */
const REPORT_UNIT = {
  people: 'people', docs: 'documents', roles: 'roles', quiz: 'quizzes', file: 'documents',
};

function ReportsView({ report, setupId, panelName, builtAt, onOpenQuiz }) {
  const [tab, setTab] = useState('people');
  const [who, setWho] = useState(null);
  const [role, setRole] = useState(null);         // the role the People tab is narrowed to
  const [certFor, setCertFor] = useState(null);   // the person whose certificate is open
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(() => {
    const saved = parseInt(localStorage.getItem(REPORT_PAGE_SIZE_KEY), 10);
    return PAGE_SIZES.includes(saved) ? saved : 25;
  });
  const { people } = report;
  const headBox = useStickyHead();

  const person = who ? people.find((p) => p.uid === who) : people[0];

  /* ------------------------------------------------------------------ where these come from
   *
   * `report.docs` is no longer sent to the browser (`GET /report?docs=none`): one entry per
   * (document x role x person) obligation is the whole weight of a large snapshot, and these
   * three tables never needed the cells — only totals grouped over them.
   *
   * So the two GROUPED tables are computed server-side over every document and travel with the
   * report (`training_query.role_report` / `quiz_report`). That is deliberate rather than
   * convenient: a roll-up computed from a PAGE would silently describe the 25 rows on screen as
   * if they were the panel, and both tables are bounded by the number of roles and the number
   * of quizzes — neither of which grows with the library.
   *
   * The Documents tab is the one that is genuinely per-document, so it is paged by the server
   * like the grid, over the same endpoint, `sort=gaps` (worst first) and `scope=docs` — the
   * attention rows are excluded there so its totals reconcile with the funnel.
   */
  const roleRows = useMemo(() => report.role_report || [], [report.role_report]);
  const quizRows = useMemo(() => report.quiz_report || [], [report.quiz_report]);

  const [docData, setDocData] = useState(null);
  const docSeqRef = useRef(0);
  useEffect(() => {
    if (tab !== 'docs') return undefined;
    const seq = ++docSeqRef.current;      // only the newest response may write
    let cancelled = false;
    (async () => {
      try {
        const params = new URLSearchParams({
          scope: 'docs', sort: 'gaps', page: String(page), limit: String(pageSize),
        });
        const resp = await fetch(`/api/training/${setupId}/documents?${params.toString()}`,
                                 { credentials: 'include' });
        const d = await resp.json().catch(() => ({}));
        if (cancelled || seq !== docSeqRef.current || !resp.ok) return;
        setDocData(d);
      } catch (e) { /* the table keeps what it last drew rather than blanking */ }
    })();
    return () => { cancelled = true; };
  }, [tab, page, pageSize, setupId]);

  // The People tab doubles as the Roles drill-down: picking a role narrows it to that role's
  // members rather than opening a second, near-identical list.
  const peopleRows = useMemo(
    () => (role ? people.filter((p) => p.role === role) : people),
    [people, role],
  );

  /* The Documents tab's rows come from the server ALREADY paged; the other tabs are bounded
     lists (people, roles, quizzes) and are still sliced here. `serverPaged` is what keeps the
     one pager below honest about which of the two it is looking at. */
  const serverPaged = tab === 'docs';
  const rows = tab === 'people' ? peopleRows
    : tab === 'docs' ? ((docData && docData.rows) || [])
      : tab === 'roles' ? roleRows
        : tab === 'quiz' ? quizRows
          : (person ? person.docs : []);

  /* Whether to show the Quiz column at all. An account that has written no quiz would
     otherwise get four tables with an all-dashes column in them, which reads as data that
     failed to load rather than as a feature it isn't using. The three-state signal is the
     funnel's, so an old snapshot says nothing rather than claiming zero. */
  const showQuiz = !!(report.funnel && report.funnel.quiz && report.funnel.quiz.configured
    && report.funnel.quiz.expected > 0);

  /* The Quizzes TAB is gated one step earlier than that column, on purpose. `expected > 0`
     means somebody owes a quiz today — and the worst state this tab exists to show is the one
     where every quiz has been left behind on a superseded revision, which makes `expected`
     exactly 0. Gating the tab on it would hide the finding precisely when it is total. */
  const qf = (report.funnel || {}).quiz;
  const showQuizTab = !!(qf && qf.configured && (qf.quizzes > 0 || quizRows.length > 0));
  useEffect(() => {
    if (!showQuizTab && tab === 'quiz') setTab('people');
  }, [showQuizTab, tab]);

  // Switching tab, opening a different person's file, narrowing to a role, or changing the page
  // size can all leave you on a page that no longer exists.
  useEffect(() => { setPage(1); }, [tab, who, role, pageSize]);
  const total = serverPaged ? ((docData && docData.total) || 0) : rows.length;
  const totalPages = serverPaged
    ? ((docData && docData.pages) || 1)
    : Math.max(1, Math.ceil(rows.length / pageSize));
  const safePage = serverPaged ? ((docData && docData.page) || 1) : Math.min(page, totalPages);
  const start = serverPaged ? ((docData && docData.start) || 0) : (safePage - 1) * pageSize;
  const pageRows = serverPaged ? rows : rows.slice(start, start + pageSize);

  const changePageSize = (n) => {
    setPageSize(n);
    localStorage.setItem(REPORT_PAGE_SIZE_KEY, String(n));
  };

  const unit = REPORT_UNIT[tab];

  return (
    <div className="tm-panel">
      <div className="tm-subtabs">
        {[['people', 'People with issues'], ['docs', 'Documents with issues'],
          ['roles', 'Roles'], ...(showQuizTab ? [['quiz', 'Quizzes']] : []),
          ['file', 'Training file']]
          .map(([k, label]) => (
            <button key={k} className={tab === k ? 'active' : ''} onClick={() => setTab(k)}>{label}</button>
          ))}
      </div>

      <div className="tm-gridwrap tm-gridwrap--sticky" ref={headBox}>
        {tab === 'people' && role && (
          <div className="tm-repfilter">
            Showing the <b>{role}</b> role — {peopleRows.length} of {people.length} people.
            <button type="button" onClick={() => setRole(null)}>Show everyone</button>
          </div>
        )}

        {tab === 'people' && (
          <table className="tm-rep">
            <thead>
              <tr>
                <th>Person</th><th>Role</th>
                <th className="num">Missing</th><th className="num">Open</th><th className="num">Signed</th>
                {showQuiz && <th className="num">Quiz</th>}
                <th style={{ width: 170 }}>Coverage</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((p) => {
                const t = p.miss + p.open + p.done;
                const pc = t ? Math.round((p.done / t) * 100) : 0;
                return (
                  <tr key={p.uid} className="tm-clickable" onClick={() => { setWho(p.uid); setTab('file'); }}>
                    <td className="tm-who">{p.name}<span>{p.uid}</span></td>
                    <td>{p.role}</td>
                    <td className="num">{p.miss ? <span className="tm-pill p-miss">{p.miss}</span> : <span className="tm-zero">0</span>}</td>
                    <td className="num">{p.open ? <span className="tm-pill p-open">{p.open}</span> : <span className="tm-zero">0</span>}</td>
                    <td className="num">{p.done ? <span className="tm-pill p-ok">{p.done}</span> : <span className="tm-zero">0</span>}</td>
                    {/* Deliberately NOT part of Coverage. Coverage is the signature rate, and
                        folding a quiz into it would let a signed-and-unquizzed obligation and a
                        quizzed-but-unsigned one produce the same percentage. */}
                    {showQuiz && <td className="num"><QuizTally q={p.quiz} /></td>}
                    <td><Meter pct={pc} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {tab === 'docs' && (
          <table className="tm-rep">
            <thead>
              <tr>
                <th>Document</th><th>Rev</th>
                <th className="num">Expected</th><th className="num">Assigned</th><th className="num">Signed</th>
                {showQuiz && <th className="num">Quiz</th>}
                <th style={{ width: 170 }}>Coverage</th><th>Finding</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((d, di) => {
                const e = d.cells.length;
                const m = countBy(d, 'miss');
                const o = countBy(d, 'open');
                const s = countBy(d, 'done');
                const pc = e ? Math.round((s / e) * 100) : 0;
                const qOutstanding = d.quiz && d.quiz.status === 'published'
                  ? d.quiz.eligible - d.quiz.passed : 0;
                return (
                  /* Position is in the key on purpose — a tenant that repeats a document key
                     must not give two rows the same React key (see Critical Note #24). */
                  <tr key={`${d.key}-${start + di}`}>
                    <td className="tm-who">{d.name}<span>
                      {d.url ? (
                        <a className="tm-link" href={d.url} target="_blank" rel="noopener noreferrer"
                           title="Open this document in Orcanos">{d.key}</a>
                      ) : d.key} · {d.nroles} roles
                    </span></td>
                    <td>{d.rev}</td>
                    <td className="num">{e}</td>
                    <td className="num">{e - m}</td>
                    <td className="num">{s}</td>
                    {showQuiz && (
                      <td className="num">
                        {d.quiz && d.quiz.status === 'published' ? <QuizTally q={d.quiz} />
                          : d.quiz && d.quiz.status === 'draft'
                            ? <span className="tm-pill p-na" title="A quiz is being written for this revision but has not been published, so nobody is being offered it.">draft</span>
                            : d.quiz_stale && d.quiz_stale.status === 'published'
                              ? (
                                <span
                                  className="tm-pill p-miss"
                                  title={`A published quiz exists for ${d.key}, filed under revision ${d.quiz_stale.revision || '—'} — this document is now on ${d.quiz_stale.doc_revision || '—'}, so it is reaching nobody.`}
                                >
                                  rev {d.quiz_stale.revision || '—'}
                                </span>
                              )
                              : <span className="tm-dash">—</span>}
                      </td>
                    )}
                    <td><Meter pct={pc} /></td>
                    <td>
                      {/* "Complete" has to mean complete. A document everyone has signed and
                          nobody has passed the quiz on is not finished, and reporting it as
                          such is precisely the false all-clear this column exists to avoid. */}
                      {m ? <span className="tm-pill p-miss">{m} never assigned</span>
                        : o ? <span className="tm-pill p-open">{o} open</span>
                          : qOutstanding > 0
                            ? <span className="tm-pill p-open">{qOutstanding} quiz outstanding</span>
                            : <span className="tm-pill p-ok">complete</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {tab === 'roles' && (
          <table className="tm-rep">
            <thead>
              <tr>
                <th>Role</th>
                <th className="num">People</th><th className="num">Documents</th>
                <th className="num">Missing</th><th className="num">Open</th><th className="num">Signed</th>
                {showQuiz && <th className="num">Quiz</th>}
                <th style={{ width: 170 }}>Coverage</th><th>Finding</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((r, ri) => {
                const pc = r.expected ? Math.round((r.done / r.expected) * 100) : 0;
                return (
                  <tr
                    key={`${r.role}-${start + ri}`}
                    className={r.people ? 'tm-clickable' : ''}
                    onClick={r.people ? () => { setRole(r.role); setWho(null); setTab('people'); } : undefined}
                    title={r.people ? 'Show every person in this role' : undefined}
                  >
                    <td className="tm-who">{r.role}<span>
                      {r.expected} training obligation{r.expected === 1 ? '' : 's'}
                    </span></td>
                    <td className="num">{r.people || <span className="tm-zero">0</span>}</td>
                    <td className="num">{r.docs}</td>
                    <td className="num">{r.miss ? <span className="tm-pill p-miss">{r.miss}</span> : <span className="tm-zero">0</span>}</td>
                    <td className="num">{r.open ? <span className="tm-pill p-open">{r.open}</span> : <span className="tm-zero">0</span>}</td>
                    <td className="num">{r.done ? <span className="tm-pill p-ok">{r.done}</span> : <span className="tm-zero">0</span>}</td>
                    {showQuiz && <td className="num"><QuizTally q={r.q} /></td>}
                    {/* A role nobody holds has no denominator — 0 % would read as a coverage
                        failure by people who do not exist. */}
                    <td>{r.expected ? <Meter pct={pc} /> : <span className="tm-dash">—</span>}</td>
                    <td>
                      {!r.people ? <span className="tm-pill p-miss">no user profile</span>
                        : r.miss ? <span className="tm-pill p-miss">{r.miss} never assigned</span>
                          : r.open ? <span className="tm-pill p-open">{r.open} open</span>
                            : (r.q.eligible - r.q.passed) > 0
                              ? <span className="tm-pill p-open">{r.q.eligible - r.q.passed} quiz outstanding</span>
                              : <span className="tm-pill p-ok">complete</span>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        {tab === 'quiz' && (
          <>
            {/* What the table below cannot say, because it is a list of quizzes that EXIST.
                The authoring gap is the opposite — documents with no quiz at all — and it is
                counted in people as well as documents, because one document with forty
                trainees is a bigger hole than ten with two. The last two lines are results
                that exist and are reaching no compliance figure; both are named, never
                dropped (Critical Note #29). */}
            <div className="tm-quizsum">
              <span>
                <b>{qf.published}</b> published,{' '}<b>{qf.drafts}</b> draft
                {qf.drafts === 1 ? '' : 's'} — covering <b>{qf.docs_with_quiz}</b>{' '}
                {qf.docs_with_quiz === 1 ? 'document' : 'documents'} in this panel.
              </span>
              {qf.docs_no_quiz > 0 && (
                <span title="A quiz is written per document but owed per person — this is the same gap counted both ways.">
                  <b>{qf.docs_no_quiz}</b>{' '}
                  {qf.docs_no_quiz === 1 ? 'document has' : 'documents have'} no quiz —{' '}
                  <b>{qf.no_quiz}</b> {qf.no_quiz === 1 ? 'person' : 'people'} with nothing to sit.
                </span>
              )}
              {qf.unmatched_total > 0 && (
                <span title="A quiz is sat under the Orcanos login name; the matrix is keyed on the user profile's Assigned To. Nothing is joined on a resemblance — fix the identity and these passes will count.">
                  <b>{qf.unmatched_total}</b> attempt{qf.unmatched_total === 1 ? '' : 's'} match
                  nobody in this panel.
                </span>
              )}
              {qf.pending_writeback > 0 && (
                <span title="A pass that never becomes a record in Orcanos is a compliance finding, so it is surfaced rather than left in the outbox.">
                  <b>{qf.pending_writeback}</b> pass
                  {qf.pending_writeback === 1 ? ' has' : 'es have'} not reached Orcanos yet.
                </span>
              )}
            </div>
            <table className="tm-rep">
              <thead>
                <tr>
                  <th>Quiz</th><th>Document</th><th>Quiz rev</th>
                  <th className="num">Can sit</th><th className="num">Passed</th>
                  <th className="num">Outstanding</th><th className="num">To sign</th>
                  <th style={{ width: 170 }}>Coverage</th><th>Finding</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((r, ri) => {
                  const q = r.q;
                  const live = r.kind === 'live' && r.status === 'published' && !!q;
                  const outstanding = live ? (q.eligible || 0) - (q.passed || 0) : 0;
                  const pc = live && q.eligible ? Math.round((q.passed / q.eligible) * 100) : 0;
                  const open = onOpenQuiz
                    ? () => onOpenQuiz(r.id, r.status === 'draft' ? 'questions' : 'results')
                    : null;
                  return (
                    <tr
                      key={`${r.id}-${start + ri}`}
                      className={open ? 'tm-clickable' : ''}
                      onClick={open || undefined}
                      title={open ? 'Open this quiz' : undefined}
                    >
                      <td className="tm-who">{r.title || `Quiz ${r.id}`}<span>
                        {live
                          ? `${q.questions} question${q.questions === 1 ? '' : 's'} · pass mark ${q.pass_mark}%`
                          : r.status === 'draft' && q
                            ? `${q.questions} question${q.questions === 1 ? '' : 's'} · not published`
                            : `filed under rev ${r.revision || '—'}`}
                      </span></td>
                      <td className="tm-who">
                        {r.doc ? r.doc.name : <span className="tm-muted">not in this panel</span>}
                        <span>
                          {r.doc && r.doc.url ? (
                            <a className="tm-link" href={r.doc.url} target="_blank" rel="noopener noreferrer"
                               onClick={(e) => e.stopPropagation()}
                               title="Open this document in Orcanos">{r.docKey}</a>
                          ) : r.docKey}
                        </span>
                      </td>
                      <td>
                        {r.revision || '—'}
                        {/* The whole of the stale finding, in the one place it is provable:
                            the quiz's revision beside the revision the document is on now. */}
                        {r.kind === 'stale' && r.docRev && (
                          <span className="tm-muted"> · doc on {r.docRev}</span>
                        )}
                      </td>
                      <td className="num">{live ? q.eligible : <span className="tm-dash">—</span>}</td>
                      <td className="num">
                        {live
                          ? (q.passed ? <span className="tm-pill p-ok">{q.passed}</span> : <span className="tm-zero">0</span>)
                          : <span className="tm-dash">—</span>}
                      </td>
                      <td className="num">
                        {live
                          ? (outstanding > 0 ? <span className="tm-pill p-open">{outstanding}</span> : <span className="tm-zero">0</span>)
                          : <span className="tm-dash">—</span>}
                      </td>
                      {/* Never folded into Outstanding: these people have not signed their
                          training task, so the quiz has not opened for them. Counting them as
                          an outstanding quiz reports the same person twice, under two owners
                          who would each think it was the other's. */}
                      <td className="num">
                        {live
                          ? (q.locked ? <span className="tm-pill p-na" title="These people owe this quiz but have not signed their training task, so it has not opened for them.">{q.locked}</span> : <span className="tm-zero">0</span>)
                          : <span className="tm-dash">—</span>}
                      </td>
                      <td>
                        {live && q.eligible ? <Meter pct={pc} /> : <span className="tm-dash">—</span>}
                      </td>
                      <td>
                        {r.kind === 'stale' ? (
                          <span
                            className="tm-pill p-miss"
                            title={`This quiz is filed under revision ${r.revision || '—'}, and the document is now on ${r.docRev || '—'}. Trainees owe the current revision, so it is offered to nobody — copy it forward to ${r.docRev || 'the current revision'}.`}
                          >
                            reaching nobody
                          </span>
                        ) : r.kind === 'away' ? (
                          <span
                            className="tm-pill p-na"
                            title="This quiz is for a document this panel does not cover. Quizzes belong to the account; a panel is one filter over the library — so this is not a finding here."
                          >
                            outside this panel
                          </span>
                        ) : r.status === 'draft' ? (
                          <span className="tm-pill p-na" title="A draft is offered to nobody. Publish it to put it in front of the people who owe this document.">
                            not published
                          </span>
                        ) : !q || !q.expected ? (
                          <span className="tm-pill p-na" title="Nobody in this panel owes training on this document's current revision, so there is nobody to sit this quiz.">
                            no audience
                          </span>
                        ) : !q.eligible ? (
                          <span className="tm-pill p-open" title="Everybody who owes this quiz has still to sign their training task — reading and signing is the training, the quiz checks what was understood.">
                            {q.locked} to sign first
                          </span>
                        ) : outstanding > 0 ? (
                          <span className="tm-pill p-open">{outstanding} outstanding</span>
                        ) : (
                          <span className="tm-pill p-ok">complete</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {quizRows.length === 0 && (
                  <tr>
                    <td colSpan={9} className="tm-muted">
                      This account has quizzes, but none of them is filed against a document in
                      this panel.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </>
        )}

        {tab === 'file' && person && (
          <>
            <div className="tm-file-head">
              <div className="tm-avatar">
                {(person.name || '?').split(' ').map((x) => x[0]).join('').slice(0, 2)}
              </div>
              <div>
                <div className="tm-file-name">{person.name}</div>
                <div className="tm-file-sub">{person.role} · {person.uid}</div>
              </div>
              <div className="tm-file-stats">
                <div><div className="v ok">{person.done}</div><div className="l">Signed</div></div>
                <div><div className="v">{person.open}</div><div className="l">Open</div></div>
                <div><div className="v miss">{person.miss}</div><div className="l">Missing</div></div>
                {/* A fourth figure only when this person actually owes a quiz — an all-zero
                    tile beside three real ones reads as a failure rather than as absence. */}
                {showQuiz && person.quiz && person.quiz.expected > 0 && (
                  <div title={`${person.quiz.passed} of ${person.quiz.eligible} quizzes this person can sit have been passed`
                    + (person.quiz.locked ? `; ${person.quiz.locked} have not opened yet because the task is unsigned` : '')}>
                    <div className={`v ${person.quiz.passed >= person.quiz.eligible ? 'ok' : ''}`}>
                      {person.quiz.passed}<span className="tm-of">/{person.quiz.eligible}</span>
                    </div>
                    <div className="l">Quizzes passed</div>
                  </div>
                )}
              </div>
              <button
                type="button"
                className="tm-cert-btn"
                onClick={() => setCertFor(person)}
                title="Open a printable training compliance certificate for this person"
              >
                📜 Training Certificate
              </button>
            </div>
            <table className="tm-rep">
              <thead>
                <tr>
                  <th>Document</th><th>Rev</th><th>Status</th>
                  {showQuiz && <th>Quiz</th>}
                  <th>Task</th><th>Due</th>
                </tr>
              </thead>
              <tbody>
                {pageRows.map((d, i) => (
                  <tr key={`${d.k}-${start + i}`}>
                    <td className="tm-who">{d.n}<span>
                      {d.url ? (
                        <a className="tm-link" href={d.url} target="_blank" rel="noopener noreferrer"
                           title="Open this document in Orcanos">{d.k}</a>
                      ) : d.k}
                    </span></td>
                    <td>{d.rev}</td>
                    <td>
                      {d.st === 'done' ? <span className="tm-pill p-ok">✓ Signed</span>
                        : d.st === 'open' ? <span className="tm-pill p-open">◷ {d.ts || 'Open'}</span>
                          : <span className="tm-pill p-miss">? Missing</span>}
                    </td>
                    {/* The person's own quiz standing on this document, in its own column —
                        never merged into Status, which is what the SIGNATURE says. */}
                    {showQuiz && (
                      <td>{d.q ? <QuizChip q={d.q} compact /> : <span className="tm-dash">—</span>}</td>
                    )}
                    <td className="tm-mono">
                      {d.tk ? (d.turl ? (
                        <a className="tm-link" href={d.turl} target="_blank" rel="noopener noreferrer"
                           title="Open this training task in Orcanos">{d.tk}</a>
                      ) : d.tk) : '—'}
                    </td>
                    <td className="tm-muted">{d.due || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>

      {total > 0 && (
        <div className="tm-foot">
          <div className="tm-paging">
            <select
              className="tm-rows-select"
              value={pageSize}
              onChange={(e) => changePageSize(parseInt(e.target.value, 10))}
              aria-label="Rows per page"
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n} per page</option>)}
            </select>
            <button disabled={safePage <= 1} onClick={() => setPage(safePage - 1)}>← Prev</button>
            <span className="tm-pageno">Page {safePage} of {totalPages}</span>
            <button disabled={safePage >= totalPages} onClick={() => setPage(safePage + 1)}>Next →</button>
            <span className="tm-showing">
              Showing {pageRows.length ? start + 1 : 0}–{start + pageRows.length} of{' '}
              {total} {unit}
            </span>
          </div>
        </div>
      )}

      {certFor && (
        <TrainingCertificate
          person={certFor}
          setupId={setupId}
          panelName={panelName}
          builtAt={builtAt}
          onClose={() => setCertFor(null)}
        />
      )}
    </div>
  );
}

/* ------------------------------------------------------------------- page */

function TrainingPanel({ isAdmin = false }) {
  const navigate = useNavigate();
  const { setupId } = useParams();

  const [report, setReport] = useState(null);
  const [panelName, setPanelName] = useState('');
  const [builtAt, setBuiltAt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [building, setBuilding] = useState(false);
  // Which of the two the running job is. They share `building` (one job at a time, one
  // progress bar) but must NOT share a label: a refresh that says "Rebuilding" for four
  // minutes because it escalated teaches people the button is broken.
  const [refreshing, setRefreshing] = useState(false);
  const [progress, setProgress] = useState(null);   // { pct, message } while a build runs
  const [error, setError] = useState('');
  const [view, setView] = useState(() => sessionStorage.getItem('trainingView') || 'matrix');
  const [search, setSearch] = useState('');
  // The state chips + approval period are collapsed by DEFAULT — they are a narrowing tool, not
  // a permanent header. `filterSummary` is what the Matrix reports back as active while they are
  // hidden: a filter that narrows the list silently is exactly the thing this panel must not do,
  // so the toggle names the narrowing on its face instead of only lighting up.
  const [filtersOpen, setFiltersOpen] = useState(() => {
    try { return sessionStorage.getItem('trainingFiltersOpen') === '1'; } catch { return false; }
  });
  const [filterSummary, setFilterSummary] = useState('');
  const [elapsed, setElapsed] = useState(0);
  const [maximized, setMaximized] = useState(() => {
    try { return sessionStorage.getItem('trainingMaximized') === '1'; } catch { return false; }
  });
  // Fit to screen — the trace matrix's ⇥⇤ toggle; remembered per browser.
  const [fitScreen, setFitScreen] = useState(() => {
    try { return localStorage.getItem('trainingFitScreen') === '1'; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem('trainingFitScreen', fitScreen ? '1' : '0'); } catch (e) { /* ignore */ }
  }, [fitScreen]);
  const [showExport, setShowExport] = useState(false);
  const [exportPct, setExportPct] = useState(null);   // non-null ⇒ the MODAL is driving progress
  const [exportJobId, setExportJobId] = useState(null);
  const showExportRef = useRef(false);
  const exportPollBusyRef = useRef(false);
  const downloadedJobsRef = useRef(new Set());
  const alive = useRef(true);
  const stopPollRef = useRef(null);   // the running poll's cancel fn, so cleanup can always reach it
  const autoBuiltRef = useRef(false); // the automatic first build has been kicked off once

  // ⚠️ `alive.current` MUST be set true here, not only at useRef(true).
  //
  // React StrictMode (dev) mounts, unmounts and re-mounts every component once. The ref
  // survives that — it is the SAME component instance — so a cleanup that only ever sets
  // it false leaves it false FOREVER on the second mount. Every `if (!alive.current) return`
  // then silently swallows its setState, and the page sits on "Building the report" while
  // the build actually completes on the server. That was exactly this bug: invisible in
  // any API test, reproducible only in a real dev browser.
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
      if (stopPollRef.current) stopPollRef.current();
    };
  }, []);

  useEffect(() => { showExportRef.current = showExport; }, [showExport]);

  /**
   * Finish an export whose modal was closed mid-flight ("Close & Continue Working").
   *
   * Only runs when the modal is NOT driving progress, so exactly one poller is ever live. The
   * three guards are the v3.8.5 ones and all of them are load-bearing:
   *   exportPollBusyRef  — `setInterval` doesn't wait for `await fetch`, so without it the ticks
   *                        queue and every one of them downloads when the job completes.
   *   downloadedJobsRef  — one file per job, whatever else happens.
   *   showExportRef      — while the modal is open IT owns the download; this poller still
   *                        clears the job id, so closing afterwards can't hand over a second copy.
   */
  useEffect(() => {
    if (!exportJobId || exportPct !== null) return undefined;
    let stopped = false;
    const timer = setInterval(async () => {
      if (stopped || exportPollBusyRef.current) return;
      exportPollBusyRef.current = true;
      try {
        const r = await fetch(`/api/matrix/export-jobs/${exportJobId}`, { credentials: 'include' });
        if (stopped) return;
        if (!r.ok) {                       // 404 ⇒ the job is gone; stop quietly
          stopped = true; clearInterval(timer); setExportJobId(null); return;
        }
        const job = await r.json();
        if (stopped) return;
        if (job.status === 'completed') {
          stopped = true; clearInterval(timer); setExportJobId(null);
          if (showExportRef.current || downloadedJobsRef.current.has(exportJobId)) return;
          downloadedJobsRef.current.add(exportJobId);
          const dl = await fetch(`/api/matrix/export-jobs/${exportJobId}/download`,
            { credentials: 'include' });
          if (!dl.ok) return;
          const blob = await dl.blob();
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = job.filename || `training-${exportJobId}.html`;
          document.body.appendChild(a); a.click(); document.body.removeChild(a);
          URL.revokeObjectURL(url);
        } else if (job.status === 'error') {
          stopped = true; clearInterval(timer); setExportJobId(null);
        }
      } catch {
        /* transient — the next tick retries */
      } finally {
        exportPollBusyRef.current = false;
      }
    }, 700);
    return () => { stopped = true; clearInterval(timer); };
  }, [exportJobId, exportPct]);

  // A first build reads three whole filters, so it is genuinely slow on a large tenant.
  // Counting the seconds is the difference between "working" and "hung".
  useEffect(() => {
    if (!loading && !building) { setElapsed(0); return undefined; }
    setElapsed(0);
    const t = setInterval(() => setElapsed((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [loading, building]);

  /* Read the cached report. GET never builds — building is POST /build, a background job.
   *
   * `docs=none` asks for everything EXCEPT the rows: the funnel, the people, the role columns,
   * the problems, and the two grouped Reports tables. The rows themselves are read a page at a
   * time from `/documents`, because `docs[].cells` holds one entry per (document x role x
   * person) obligation and is effectively the entire weight of a large snapshot. The default
   * is still `all`, so nothing else that reads this endpoint had to change. */
  const readSnapshot = useCallback(async () => {
    const resp = await fetch(`/api/training/${setupId}/report?docs=none`,
                             { credentials: 'include' });
    const data = await resp.json().catch(() => ({}));
    if (!resp.ok) throw new Error(data.detail || `Failed to read the panel (${resp.status})`);
    return data;
  }, [setupId]);

  /**
   * Watch a build that is ALREADY running on the server, and finish the page when it does.
   *
   * Split out of `startBuild` (v3.31.1) because the two entry points are genuinely
   * different: one starts a job, the other adopts one that is already in flight — the
   * build a previous mount of this page started before the user left it. The job itself
   * never stopped; only the poller did (see the mount effect below).
   *
   * The poll is re-entrancy guarded: `setInterval` does NOT wait for the previous `await
   * fetch` to return, so on a slow server the ticks queue up and every one of them acts on
   * completion at once — the bug that made a single export download 30 files (v3.8.5).
   */
  const followJob = useCallback((jobId, refresh, initial) => {
    if (stopPollRef.current) stopPollRef.current();   // never leave two pollers running
    setBuilding(true);
    setRefreshing(!!refresh);
    setError('');
    setProgress(initial || { pct: 0, message: refresh ? 'Checking what changed…' : 'Building…' });

    let timer = null;
    let busy = false;
    let stopped = false;
    let misses = 0;

    const stop = () => {
      stopped = true;
      if (timer) clearInterval(timer);
      timer = null;
      if (stopPollRef.current === stop) stopPollRef.current = null;
    };

    const finish = (msg) => {
      stop();
      if (!alive.current) return;
      if (msg) setError(msg);
      setProgress(null);
      setBuilding(false);
      setRefreshing(false);
      setLoading(false);
    };

    timer = setInterval(async () => {
      if (busy || stopped || !alive.current) return;
      busy = true;
      try {
        const r = await fetch(`/api/matrix/export-jobs/${jobId}`, { credentials: 'include' });
        if (!r.ok) {
          // A poll that never succeeds must not spin forever: the job row can be gone
          // (server restarted, job expired) and the page would sit on "Building…" for ever.
          misses += 1;
          if (misses >= 5) {
            finish(r.status === 404
              ? 'The build is no longer running — the server may have restarted. Try Rebuild.'
              : `Lost contact with the build (${r.status}). Try Rebuild.`);
          }
          return;
        }
        misses = 0;
        const s = await r.json();
        if (stopped || !alive.current) return;
        setProgress({ pct: s.progress || 0, message: s.message || 'Building…' });
        if (s.status === 'completed') {
          stop();
          const snap = await readSnapshot();
          if (!alive.current) return;
          setReport(snap.report);
          setBuiltAt(snap.built_at || null);
          finish(null);
        } else if (s.status === 'error') {
          // The job endpoint reports the reason as `error`, not `error_message`.
          finish(s.error || s.message || 'The build failed.');
        }
      } catch (e) {
        misses += 1;
        if (misses >= 5) finish('Lost contact with the build. Try Rebuild.');
      } finally {
        busy = false;
      }
    }, 1500);
    stopPollRef.current = stop;
    return stop;
  }, [readSnapshot]);

  /** Start a background build and follow it. */
  const startBuild = useCallback(async (mode) => {
    // `mode` is only ever the string 'refresh'. Every other value — including the click
    // event a bare `onClick={startBuild}` passes — means a full build, so a handler that
    // forgets to wrap the call can never accidentally ask for the cheap path.
    const refresh = mode === 'refresh';
    if (stopPollRef.current) stopPollRef.current();
    setBuilding(true);
    setRefreshing(refresh);
    setError('');
    setProgress({ pct: 0, message: refresh ? 'Checking what changed…' : 'Starting build…' });

    try {
      const resp = await fetch(`/api/training/${setupId}/${refresh ? 'refresh' : 'build'}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'X-CSRF-Token': getCsrfToken() },
      });
      const job = await resp.json().catch(() => ({}));
      if (!resp.ok) throw new Error(job.detail || `Could not start the build (${resp.status})`);
      return followJob(job.job_id, refresh,
                       { pct: 0, message: job.message || 'Starting build…' });
    } catch (err) {
      if (stopPollRef.current) stopPollRef.current();
      if (alive.current) {
        setError(err.message);
        setProgress(null);
        setBuilding(false);
        setRefreshing(false);
        setLoading(false);
      }
    }
    return undefined;
  }, [setupId, followJob]);

  /** Is one of MY builds still running on this panel? (v3.31.1)
   *
   * The build is a server thread — it does not stop because the page went away. The
   * POLL did, and that was the whole bug: leave the panel mid-rebuild, come back, and
   * the page showed the OLD snapshot with no progress bar, which reads as "it stopped".
   * Errors are swallowed on purpose: not knowing about a job must never block opening
   * the panel, it only costs the progress bar. */
  const findActiveBuild = useCallback(async () => {
    try {
      const r = await fetch(`/api/matrix/${setupId}/active-job`, { credentials: 'include' });
      if (!r.ok) return null;
      const { job } = await r.json();
      if (!job || job.status !== 'processing') return null;
      // An HTML export of this same panel is somebody else's progress bar, not a build.
      if (job.kind !== 'training_build' && job.kind !== 'training_refresh') return null;
      return job;
    } catch {
      return null;
    }
  }, [setupId]);

  // On open: show the cached snapshot instantly, re-attach to a build that is still
  // running, or start one if there is no snapshot yet.
  //
  // `autoBuiltRef` is not belt-and-braces — StrictMode runs this effect twice, and the
  // second run would POST a second build that the one-build-per-user guard answers with a
  // 429, painting an error over a build that is running perfectly well.
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError('');
      try {
        const snap = await readSnapshot();
        if (!alive.current) return;
        setPanelName(snap.name || '');
        const cached = snap.cached && snap.report;
        if (cached) {
          setReport(snap.report);
          setBuiltAt(snap.built_at || null);
          setLoading(false);
        }
        // Adopt a running build BEFORE deciding to start one — otherwise a panel with no
        // snapshot yet POSTs a second build and the 429 paints an error over a healthy job.
        const running = await findActiveBuild();
        if (!alive.current) return;
        if (running) {
          autoBuiltRef.current = true;
          followJob(running.job_id, running.kind === 'training_refresh',
                    { pct: running.progress || 0, message: running.message || 'Building…' });
        } else if (!cached && !autoBuiltRef.current) {
          autoBuiltRef.current = true;
          await startBuild();
        }
      } catch (err) {
        if (alive.current) { setError(err.message); setLoading(false); }
      }
    })();
    // The poll is cancelled by the mount effect's cleanup via stopPollRef, which — unlike a
    // local variable assigned after an await — is always populated by the time it is read.
    return undefined;
  }, [readSnapshot, startBuild, followJob, findActiveBuild]);

  useEffect(() => {
    document.body.classList.add('training-fullscreen');
    return () => document.body.classList.remove('training-fullscreen');
  }, []);

  // Maximize mode — the same affordance the trace matrix has: hide the app header and lift the
  // toolbar out of flow so the report fills the viewport. The toolbar comes back as a floating
  // overlay on top-edge hover (CSS-driven, see .training-maximized in TrainingPanel.css).
  // Persisted per session so a rebuild or a reload lands back in it.
  useEffect(() => {
    try { sessionStorage.setItem('trainingMaximized', maximized ? '1' : '0'); } catch (e) { /* ignore */ }
    document.body.classList.toggle('training-maximized', maximized);
    return () => document.body.classList.remove('training-maximized');
  }, [maximized]);

  useEffect(() => {
    try { sessionStorage.setItem('trainingFiltersOpen', filtersOpen ? '1' : '0'); } catch (e) { /* ignore */ }
  }, [filtersOpen]);

  // Esc leaves maximize — but not while typing in the search box, where Esc clears the field.
  useEffect(() => {
    if (!maximized) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      const tag = (e.target && e.target.tagName) || '';
      if (/^(INPUT|TEXTAREA|SELECT)$/.test(tag)) return;
      setMaximized(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [maximized]);

  useEffect(() => {
    const n = panelName || report?.name;
    if (n) {
      document.title = `${n} — Training Traceability`;
      return () => { document.title = 'Traceability Matrix'; };
    }
    return undefined;
  }, [panelName, report]);

  const switchView = (v) => { setView(v); sessionStorage.setItem('trainingView', v); };

  /* The chrome max mode needs, rendered in BOTH branches — the state is remembered per session,
     so a reload can land straight on the loading screen already maximized, and a hover-only
     reveal with no escape hatch would strand the user there.
     The hover zone must be the toolbar's PREVIOUS SIBLING: the reveal is a `~` selector. */
  const maxChrome = maximized && (
    <>
      <div className="tm-maxhz" aria-hidden="true" />
      <button
        type="button"
        className="tm-max-exit"
        onClick={() => setMaximized(false)}
        title="Exit maximize (Esc)"
        aria-label="Exit maximize"
      >
        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" /></svg>
        <span>Exit</span>
      </button>
    </>
  );

  if (loading) {
    const pct = progress ? progress.pct : 0;
    const parts = parseProgress(progress?.message);
    return (
      <div className="tm-page">
        {maxChrome}
        <div className="tm-toolbar">
          <button className="tm-back" onClick={() => navigate('/training')}>← Back</button>
          <span className="tm-title-name">{panelName || 'Training Traceability'}</span>
        </div>
        {error ? (
          <div className="tm-error">{error}</div>
        ) : (
          <div className="tm-loading">
            <div className="tm-loading-bar">
              <i className={pct ? 'is-determinate' : ''} style={pct ? { width: `${pct}%` } : undefined} />
            </div>
            <p><b>Building the report</b> {pct > 0 && <span className="tm-pct">{pct}%</span>}</p>
            {parts.length ? (
              <ul className="tm-progress-list">
                {parts.map((p) => (
                  <li key={p.label} className={p.done ? 'is-done' : ''}>
                    <span className="tm-pl-label">{p.label}</span>
                    <span className="tm-pl-bar">
                      <i style={{ width: `${p.pct}%` }} />
                    </span>
                    <span className="tm-pl-n">{p.detail}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p>{progress?.message || 'Reading documents, user profiles and training tasks…'}</p>
            )}
            <p className="tm-loading-time">
              {elapsed}s elapsed
              {elapsed > 45 && ' — a large task filter can take a few minutes. It is still working.'}
            </p>
            <button className="tm-btn-link" onClick={() => navigate('/training')}>
              Leave this page — the build finishes on the server
            </button>
          </div>
        )}
      </div>
    );
  }

  /* Matrix view: the page itself does not scroll — only the Documents × role grid does, so
     there is one vertical scrollbar, not the page's and the grid's side by side (2026-11-01). */
  const fitPage = view === 'matrix' && !!(report && report.funnel);

  return (
    <div className={`tm-page${fitPage ? ' tm-page--fit' : ''}`}>
      {maxChrome}
      <div className="tm-toolbar">
        <button className="tm-back" onClick={() => navigate('/training')}>← Back</button>
        <div className="tm-title">
          <span className="tm-title-name">{panelName || report?.name || 'Training Traceability'}</span>
          <button
            className="tm-icon-btn"
            title="Edit this panel"
            aria-label="Edit this panel"
            onClick={() => navigate(`/training/${setupId}/edit`)}
          >✎</button>
        </div>

        <div className="tm-vt">
          {[['matrix', 'Matrix'], ['funnel', 'Funnel'], ['reports', 'Reports']].map(([k, label]) => (
            <button key={k} className={view === k ? 'active' : ''} onClick={() => switchView(k)}>{label}</button>
          ))}
        </div>

        <button
          type="button"
          className="tm-maxbtn"
          onClick={() => setMaximized((m) => !m)}
          title={maximized ? 'Exit maximize (Esc)' : 'Maximize the working area'}
          aria-label={maximized ? 'Exit maximize' : 'Maximize the working area'}
          aria-pressed={maximized}
        >
          {maximized ? (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M6 2v4H2M10 2v4h4M6 14v-4H2M10 14v-4h4" /></svg>
          ) : (
            <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" aria-hidden="true"><path d="M2 6V2h4M14 6V2h-4M2 10v4h4M14 10v4h-4" /></svg>
          )}
        </button>

        {view === 'matrix' && (
          <>
            <button
              type="button"
              className="tm-maxbtn"
              onClick={() => setFitScreen((f) => !f)}
              title={fitScreen ? 'Back to regular size' : 'Fit more roles on one screen'}
              aria-label={fitScreen ? 'Back to regular size' : 'Fit more roles on one screen'}
              aria-pressed={fitScreen}
            >
              {fitScreen ? (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1.5 3v10M14.5 3v10M4 8h8M6 6L4 8l2 2M10 6l2 2-2 2" /></svg>
              ) : (
                <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M1.5 3v10M14.5 3v10M3.5 8h3M9.5 8h3M5 6l1.5 2L5 10M11 6L9.5 8l1.5 2" /></svg>
              )}
            </button>
            <input
              className="tm-search"
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search document, person or role…"
            />
            {/* Collapsed by default. While the row is hidden the button CARRIES the active
                narrowing as text — a compliance panel must never show a shortened list with no
                visible reason for it. */}
            <button
              type="button"
              className={`tm-filtbtn ${filtersOpen ? 'on' : ''} ${filterSummary ? 'narrowed' : ''}`}
              onClick={() => setFiltersOpen((f) => !f)}
              aria-pressed={filtersOpen}
              aria-label={filtersOpen ? 'Hide filters' : 'Show filters'}
              title={filtersOpen
                ? 'Hide the filters and scorecards'
                : (filterSummary ? `Filters — showing: ${filterSummary}` : 'Show the filters and scorecards')}
            >
              <FilterIcon />
              {!filtersOpen && filterSummary && (
                <span className="tm-filtbtn-sum">{filterSummary}</span>
              )}
            </button>
          </>
        )}

        <span className="tm-spacer" />
        {builtAt && <span className="tm-built">Snapshot {String(builtAt).replace('T', ' ').slice(0, 16)}</span>}
        {/* What the last refresh actually did. A refresh that quietly escalated to a full
            rebuild, or one that could not run because a filter has no Updated Date column,
            must SAY so — "refreshed" over a four-minute rebuild, or over a panel that cannot
            be refreshed at all, is the kind of quiet inaccuracy this product exists to catch. */}
        {report?.refresh && (
          <span
            className="tm-built"
            title={report.refresh.escalated
              ? `Full rebuild: ${report.refresh.reason}`
              : `${report.refresh.read || 0} row(s) re-read, ${report.refresh.held || 0} held from the last build`}
          >
            {report.refresh.escalated
              ? '· rebuilt in full'
              : `· ${report.refresh.changed || 0} changed`}
          </span>
        )}
        {/* Only offered once a snapshot exists — an export renders the snapshot and never
            builds one, so with nothing cached the button could only ever raise an error. */}
        {report && (
          <button
            className="tm-tbtn"
            onClick={() => setShowExport(true)}
            disabled={building}
            title={building ? 'Wait for the rebuild to finish' : 'Export this report'}
          >
            {exportPct === null ? '⬇ Export…' : `⬇ Exporting ${exportPct}%`}
          </button>
        )}
        {/* Refresh reads only the rows Orcanos says changed since the last build — seconds
            rather than minutes on a large tenant. It falls back to a full rebuild on its own
            when the incremental answer can't be trusted (a filter with no Updated Date
            column, or something deleted), and says so when it does, so the two buttons never
            disagree about what the panel is showing. */}
        {report && (
          <button
            className="tm-tbtn"
            onClick={() => startBuild('refresh')}
            disabled={building}
            title="Re-read only what changed in Orcanos since the last build"
          >
            <span className={refreshing ? 'tm-spin' : ''}>⤾</span>{' '}
            {refreshing
              ? `Refreshing… ${progress ? `${progress.pct}%` : `${elapsed}s`}`
              : 'Refresh'}
          </button>
        )}
        <button className="tm-tbtn" onClick={startBuild} disabled={building}>
          <span className={building && !refreshing ? 'tm-spin' : ''}>⟳</span>{' '}
          {building && !refreshing
            ? `Rebuilding… ${progress ? `${progress.pct}%` : `${elapsed}s`}`
            : 'Rebuild'}
        </button>
      </div>

      {showExport && (
        <ExportModal
          setupId={setupId}
          panelName={panelName || report?.name || 'Training'}
          excelUrl={`/api/training/${setupId}/export/excel`}
          htmlUrl={`/api/training/${setupId}/export/html-background`}
          requestBody={{}}
          htmlNote={'The Documents × role grid exactly as it appears here — every document, '
            + 'expanding to the people and their previous revisions.'}
          excelNote={'One row per document, role and person — the drill-down already expanded, '
            + 'current revision only.'}
          onProgressChange={setExportPct}
          onClose={() => { setShowExport(false); setExportPct(null); }}
        />
      )}

      {error && <div className="tm-error">{error}</div>}

      {report && (report.problems || []).length > 0 && (
        <div className="tm-problems">
          <b>Read this report with these caveats:</b>
          <ul>{report.problems.map((p, i) => <li key={i}>{p}</li>)}</ul>
        </div>
      )}

      {report && report.funnel ? (
        <div className="tm-body">
          {view === 'matrix' && (
            <MatrixView
              report={report}
              fit={fitScreen}
              search={search}
              setupId={setupId}
              panelName={panelName}
              builtAt={builtAt}
              /* The quiz screens are admin-only, so only an admin is handed a link into
                 them — a chip that bounced anyone else back to the training home would be
                 worse than no chip. `?tab=results` because from here the question is
                 always "who passed", not "what does it ask". */
              onOpenQuiz={isAdmin
                ? (id, tab) => navigate(`/training/quizzes/${id}?tab=${tab || 'results'}`)
                : null}
              filtersOpen={filtersOpen}
              onFilterSummary={setFilterSummary}
            />
          )}
          {view === 'funnel' && <FunnelView report={report} />}
          {view === 'reports' && (
            <ReportsView
              report={report}
              setupId={setupId}
              panelName={panelName}
              builtAt={builtAt}
              /* Same gate as the Matrix's chips: the quiz screens are admin-only, so only an
                 admin is handed a link into them. A row that bounced anyone else back to the
                 training home would be worse than a row that does nothing. */
              onOpenQuiz={isAdmin
                ? (id, tab) => navigate(`/training/quizzes/${id}?tab=${tab || 'results'}`)
                : null}
            />
          )}
        </div>
      ) : !building && (
        /* No snapshot and nothing running — never leave the page blank. */
        <div className="tm-loading">
          <p><b>No report yet</b></p>
          <p>Building reads your document, user-profile and training-task filters end to end.</p>
          <button className="tm-tbtn" onClick={startBuild}>⟳ Build the report</button>
        </div>
      )}
    </div>
  );
}

export default TrainingPanel;
